/** Правая часть: заголовок комнаты, лента, поле ввода. */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { getLangSnapshot, useT, useTF } from "../i18n";
import { NotificationCountType, RoomEvent, type MatrixClient, type MatrixEvent, type Room } from "matrix-js-sdk";

import { markRoomRead, newestReceiptTarget } from "../matrix/readReceipts";
import { activeCallIsVideo } from "../notifications/callIntent";
import { declineActiveCall } from "../notifications/declineCall";
import { avatarOf, directRoomIds, isEncrypted } from "../matrix/rooms";
import {
  editMessage,
  humanError,
  membersOf,
  redactMessage,
  sendAttachment,
  sendReaction,
  sendReply,
  sendTextMessage,
  sendVoiceMessage,
  withRateLimit,
  type RichSegment,
} from "../matrix/roomActions";
import { canRedactEvent, useRoomPermissions } from "../matrix/permissions";
import {
  buildReactions,
  buildThreadReactions,
  buildTimeline,
  canLoadOlder,
  loadOlder,
  type ReactionChip,
  type TimelineItem,
} from "../matrix/timeline";
import { CallCaptions } from "./CallCaptions";
import { Composer } from "./Composer";
import { Zoomable } from "./Zoomable";
import { Dialog, DialogButton, useAsyncAction } from "./Dialog";
import {
  IconChevronLeft,
  IconMic,
  IconMicOff,
  IconMinimize,
  IconMore,
  IconPhone,
  IconPhoneOff,
  IconScreenShare,
  IconScreenShareOff,
  IconVideo,
  IconVideoOff,
  IconChevronUp,
  IconUserPlus,
  IconSpeaker,
  IconCameraFlip,
} from "./icons";
import { RoomPage } from "./RoomPage";
import { ThreadPanel } from "./ThreadPanel";
import { Timeline } from "./Timeline";
import { TypingIndicator } from "./TypingIndicator";
import { palette, s } from "./styles";
import { color, colorOf, radius, space } from "./theme";
import { debugLog } from "../debug";
import { useAuthedMedia } from "../matrix/media";
import { useCallPresence, type CallJoin, type CallParticipant, type MediaDeviceLists } from "../matrix/rtc";
import { useWindowActive } from "./useWindowActive";

export function RoomView({
  client,
  room,
  callJoin: callJoinProp,
  callMinimized = false,
  onMinimizeCall,
  onMobileBack,
  onInvite,
  onRoomLink,
  onAddParticipantFromDm,
  onLeave,
  onAcceptInvite,
  onDeclineInvite,
  onToggleFavourite,
  scrollToEventId,
  onScrollToEventHandled,
}: {
  client: MatrixClient;
  room: Room;
  /**
   * Объект звонка ЭТОЙ комнаты, спущенный сверху из App (держатель
   * ActiveCallHolder). null — звонок этой комнате сейчас не принадлежит
   * (звонка нет или он идёт в другой комнате): рисуем обычную ленту,
   * кнопки звонка неактивны. Владение звонком поднято на уровень App
   * (критичный баг 2026-09-12: раньше хук жил здесь и умирал при смене
   * комнаты, обрывая звонок).
   */
  callJoin: CallJoin | null;
  /**
   * Звонок СВЁРНУТ в мини-фрейм по кнопке «написать» (дополнение
   * 2026-09-12 09:20): полный экран CallScreen НЕ рисуем, хотя звонок
   * идёт, — под ним открываются лента и композер, а App поверх
   * показывает CallMiniFrame. По умолчанию false: в чужих комнатах и
   * при обычном входе — полный экран, как раньше.
   */
  callMinimized?: boolean;
  /** Сворачивание в мини-фрейм (кнопка «написать» на экране звонка). */
  onMinimizeCall?: () => void;
  /**
   * МОБИЛЬНЫЙ РЕЖИМ (спека 2026-09-20, этап 2): на ≤640px чат занимает
   * весь экран, список скрыт — кнопка «‹ Назад» в шапке чата ведёт к
   * списку (закрывает комнату). undefined — десктоп, кнопки нет.
   */
  onMobileBack?: () => void;
  onInvite: () => void;
  /** «Ссылка на комнату…» на странице комнаты (Антон 16.10). */
  onRoomLink?: () => void;
  /** DM call: add a third person by creating a new group room (App decides how). */
  onAddParticipantFromDm?: () => void;
  onLeave: () => void;
  onAcceptInvite: () => void;
  onDeclineInvite: () => void;
  onToggleFavourite: () => void;
  /**
   * Событие из адреса (#/room/<id>/<eventId>), к которому надо проскроллить.
   * null — обычное открытие комнаты внизу ленты.
   */
  scrollToEventId?: string | null;
  /** Родитель сбрасывает scrollToEventId после того, как мы его обработали. */
  onScrollToEventHandled?: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [reactions, setReactions] = useState<Map<string, ReactionChip[]>>(new Map());
  const [older, setOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const encrypted = isEncrypted(room);
  const invite = room.getMyMembership() === "invite";
  const busyRef = useRef(false);
  // Страница комнаты (правая панель, как в Telegram Desktop). Открывается
  // кликом по ВСЕЙ зоне «аватар+название+участники» в шапке. Название
  // состояния говорит ЧТО это (page), а не как попали (header).
  const [showRoomPage, setShowRoomPage] = useState(false);
  const pinned = Boolean(room.tags?.["m.favourite"]);
  // Единый объект прав: все кнопки и поле ввода читают его, а не считают сами.
  const perms = useRoomPermissions(client, room);
  const canInviteHere = perms.canInvite;
  // useRoomPushMode здесь больше не нужен: колокольчик из шапки убран
  // (Антон 18:58). Состояние push-правил читает страница комнаты сама.
  // Аватар шапки: тот же выбор, что в списке комнат (свой аватар, а для DM —
  // аватар собеседника; Антон 16.09 20:38) + авторизованная загрузка (media
  // сервера требует Bearer, голый <img src> не работает — см. media.ts).
  const avatarMxc = avatarOf(client, room);
  const avatar = useAuthedMedia(client, avatarMxc);

  // Режимы композера: ответ на сообщение или редактирование.
  const [replyTo, setReplyTo] = useState<{
    eventId: string;
    senderName: string;
    snippet: string;
    /** mxc миниатюры для цитаты-картинки/видео (Антон 2026-09-14). null — текст. */
    thumbMxc?: string | null;
    /** Имя файла для подписи под миниатюрой. */
    thumbName?: string | null;
  } | null>(null);
  const [editing, setEditing] = useState<{ eventId: string; originalBody: string } | null>(null);
  /** Открытый диалог подтверждения удаления. */
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  /** Открытый тред (id корневого события). */
  // Открытый тред ПО КОМНАТАМ, а не одним значением.
  // БАГ (Антон, 2026-09-10 13:27): «если переключиться на другую комнату
  // тред пропадает». Состояние было одно на весь RoomView, и при смене
  // комнаты либо тянулось в чужую комнату, либо терялось. Теперь помним
  // для каждой комнаты свой открытый тред: ушёл и вернулся — панель на
  // месте, перешёл в другую комнату — там своё состояние.
  const [openThreadByRoom, setOpenThreadByRoom] = useState<Record<string, string>>({});
  const openThreadId = openThreadByRoom[room.roomId] ?? null;
  const setOpenThreadId = useCallback(
    (id: string | null) => {
      setOpenThreadByRoom((prev) => {
        if (id === null) {
          if (!(room.roomId in prev)) return prev;
          const next = { ...prev };
          delete next[room.roomId];
          return next;
        }
        if (prev[room.roomId] === id) return prev;
        return { ...prev, [room.roomId]: id };
      });
    },
    [room.roomId],
  );

  // Отметка прочтения улетает только после того, как Timeline подтвердил:
  // пользователь видел весь непрочитанный диапазон от хвоста до его начала.
  const windowActive = useWindowActive();
  const [reviewedThroughEventId, setReviewedThroughEventId] = useState<string | null>(null);
  const [scrollToBottomSignal, setScrollToBottomSignal] = useState(0);
  const markOwnMessageSent = useCallback(() => {
    setScrollToBottomSignal((value) => value + 1);
  }, []);

  const refresh = useCallback(() => {
    setItems(buildTimeline(room, client.getUserId() ?? ""));
    setReactions(buildReactions(room, client.getUserId() ?? ""));
    setOlder(canLoadOlder(room));
  }, [room, client]);

  // Смена комнаты сбрасывает подтверждение просмотра предыдущего чата.
  useEffect(() => {
    setReviewedThroughEventId(null);
    setReceiptError(null);
  }, [room.roomId]);

  // Протухание членств звонка (MSC3401): если человек закрыл вкладку,
  // явного выхода {} НЕ приходит вообще, и членство гаснет по expires
  // (дефолт 4 ч). События протухания в таймлайне нет — пересобираем
  // ленту по минутному таймеру, иначе карточка «идёт звонок» висела бы
  // вечно (гоча, проверенная Момо в базе стенда 2026-09-11).
  // items читаем через ref: включить его в зависимости эффекта значило
  // бы перезапускать все подписки на каждый рендер ленты.
  const itemsRef = useRef<TimelineItem[]>([]);
  itemsRef.current = items;
  useEffect(() => {
    const callExpiryTick = setInterval(() => {
      if (itemsRef.current.some((i) => i.call?.active)) refresh();
    }, 60_000);
    return () => clearInterval(callExpiryTick);
  }, [refresh]);

  // Живое обновление: любое событие таймлайна этой комнаты пересобирает ленту.
  // Отдельно слушаем LocalEchoUpdated — это переход локального эха в
  // «отправлено»/«ошибка», без него оптимистичный рендер зависнет серым.
  useEffect(() => {
    refresh();

    // У этих событий разные подписи: у Timeline/LocalEchoUpdated/Redaction
    // комната вторым аргументом, у TimelineReset — первым. Поэтому
    // два разных обработчика, а не один с `any`.
    const onRoomSecond = (_ev: MatrixEvent, r: Room | undefined) => {
      if (r?.roomId === room.roomId) refresh();
    };
    const onReset = (r: Room | undefined) => {
      if (r?.roomId === room.roomId) refresh();
    };

    client.on(RoomEvent.Timeline, onRoomSecond);
    client.on(RoomEvent.TimelineReset, onReset);
    client.on(RoomEvent.LocalEchoUpdated, onRoomSecond);
    client.on(RoomEvent.Redaction, onRoomSecond);
    // Receipt тоже пересобирает ленту: точка «непрочитано» на индикаторе
    // треда зависит от тредовых отметок, и без этой подписки она бы
    // задерживалась до следующего события таймлайна.
    client.on(RoomEvent.Receipt, onRoomSecond);

    return () => {
      client.off(RoomEvent.Timeline, onRoomSecond);
      client.off(RoomEvent.TimelineReset, onReset);
      client.off(RoomEvent.LocalEchoUpdated, onRoomSecond);
      client.off(RoomEvent.Redaction, onRoomSecond);
      client.off(RoomEvent.Receipt, onRoomSecond);
    };
  }, [client, room, refresh]);

  // При открытии комнаты подтягиваем одну страницу истории: initialSyncLimit
  // даёт всего несколько последних событий, и лента выглядит подозрительно
  // пустой. Зашифрованным комнатам это не нужно — читать нечем.
  useEffect(() => {
    if (encrypted || invite) return;
    let cancelled = false;
    (async () => {
      if (!canLoadOlder(room)) return;
      try {
        await loadOlder(client, room, 40);
        if (!cancelled) refresh();
      } catch (e) {
        if (!cancelled) setHistoryError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, room, encrypted, invite, refresh]);

  /**
   * Отметка прочтения.
   *
   * Срабатывает на любом изменении условий: открытие комнаты, возврат фокуса,
   * прокрутка вниз, приход нового сообщения при открытой комнате (items в зависимостях).
   *
   * Защита от повторных запросов — внутри markRoomRead (hasUserReadEvent):
   * прочитанное повторно не отмечается, так что частые рендеры безопасны.
   * Зашифрованные комнаты и приглашения исключены осознанно: в первых мы
   * содержимое не показываем вообще — отметить их прочитанными было бы ложью
   * другим участникам, во вторые мы ещё не вошли.
   */
  useEffect(() => {
    if (encrypted || invite) return;
    if (!windowActive || !reviewedThroughEventId) return;
    if (items.length === 0) return;
    if (newestReceiptTarget(room)?.getId() !== reviewedThroughEventId) return;

    let cancelled = false;
    (async () => {
      try {
        // Стенограмма под algDebug: факт отправки и целевой event id —
        // живой замер для подтверждения на стенде (Антон, 2026-09-23).
        debugLog(`[receipt-trace] markRoomRead → room=${room.roomId} target=${reviewedThroughEventId}`);
        await markRoomRead(client, room);
        if (!cancelled) setReceiptError(null);
      } catch (e) {
        // Ошибку показываем, а не глотаем: иначе необнулённый счётчик
        // выглядит как мистика без единой подсказки, что сломалось.
        if (!cancelled) setReceiptError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, room, encrypted, invite, windowActive, reviewedThroughEventId, items]);

  const onLoadOlder = useCallback(() => {
    if (busyRef.current) return;
    busyRef.current = true;
    setLoadingOlder(true);
    (async () => {
      try {
        const more = await loadOlder(client, room, 40);
        // Диагностика «иногда история не прокручивается» (Антон 18:57):
        // видно, что ответил сервер и осталось ли что-то дальше. Без этого
        // пустая страница и достигнутое начало комнаты выглядят одинаково.
        debugLog(
          `[history] страница получена, ещё есть=${more}, событий в таймлайне=${room.getLiveTimeline().getEvents().length}`,
        );
        setHistoryError(null);
        refresh();
      } catch (e) {
        // Ошибку НЕ глушим: 429/403 по истории — самая частая причина
        // «прокрутка замерла», и человек должен видеть причину, а не
        // молча упёршуюся ленту.
        debugLog(`[history] страница НЕ получена: ${(e as Error).message}`);
        setHistoryError((e as Error).message);
      } finally {
        busyRef.current = false;
        setLoadingOlder(false);
      }
    })();
  }, [client, room, refresh]);

  // Страницу комнаты, тред и режимы композера закрываем при смене комнаты:
  // оставшаяся открытой панель показала бы состав уже ДРУГОЙ комнаты без
  // всякого предупреждения.
  useEffect(() => {
    setShowRoomPage(false);
    // Режимы композера тоже сбрасываем: цитата и редактирование относятся
    // к конкретной комнате и в другой бессмысленны.
    setReplyTo(null);
    setEditing(null);
    setDeleteTarget(null);
    setOpenThreadId(null);
  }, [room.roomId]);

  /**
   * Отправка вложения. Ошибку отдаём наверх в Composer, он её и покажет:
   * там же, где пользователь только что жал кнопку.
   */
  const sendFile = useCallback(
    async (file: File, caption: string | undefined, onProgress: (p: number) => void) => {
      await sendAttachment(client, room.roomId, file, onProgress, caption);
      markOwnMessageSent();
    },
    [client, room, markOwnMessageSent],
  );

  /**
   * Отправка голосового сообщения (Антон, 2026-09-14 11:10).
   * Файл уже записан Composer'ом в ogg/opus; здесь только отправляем
   * как m.audio с org.matrix.msc1767.audio и m.voice.
   */
  const sendVoice = useCallback(
    async (file: File, duration: number, waveform: number[]) => {
      await sendVoiceMessage(client, room.roomId, file, duration, waveform);
      markOwnMessageSent();
    },
    [client, room, markOwnMessageSent],
  );

  // --- Действия над сообщениями (этапы 3-5) -------------------------------

  const handleReply = useCallback(
    (eventId: string) => {
      const item = items.find((i) => i.eventId === eventId);
      if (!item) return;
      setEditing(null); // редактирование и ответ несовместимы
      // Миниатюра в цитате (Антон 2026-09-14 10:00): если оригинал —
      // картинка или видео, передаём её mxc, чтобы композер показал
      // thumbnail. Для текста/файла thumbMxc = null.
      const thumbMxc =
        item.attachment &&
        (item.attachment.msgtype === "m.image" || item.attachment.msgtype === "m.video")
          ? item.attachment.mxc
          : null;
      setReplyTo({
        eventId,
        senderName: item.senderName,
        snippet: item.body.slice(0, 100),
        thumbMxc,
      });
    },
    [items],
  );

  const handleEdit = useCallback(
    (eventId: string, currentBody: string) => {
      setReplyTo(null); // ответ и редактирование несовместимы
      setEditing({ eventId, originalBody: currentBody });
    },
    [],
  );

  const handleDelete = useCallback((eventId: string) => {
    setDeleteTarget(eventId);
  }, []);

  const handleReactionToggle = useCallback(
    async (eventId: string, emoji: string) => {
      const chips = reactions.get(eventId) ?? [];
      const chip = chips.find((c) => c.key === emoji);
      if (chip?.mine && chip.myEventId) {
        // Снять свою реакцию = redact своего события m.reaction.
        await withRateLimit(() => client.redactEvent(room.roomId, chip.myEventId!));
      } else {
        // Поставить реакцию.
        await sendReaction(client, room.roomId, eventId, emoji);
      }
      refresh();
    },
    [client, room, reactions, refresh],
  );

  /**
   * Переключение реакции В ТРЕДЕ. Отдельный обработчик, а не общий
   * handleReactionToggle — по той же причине, по которой у треда своя
   * карта реакций: тредовых событий-целей нет в комнатной карте, и общий
   * обработчик при снятии не нашёл бы myEventId — вместо redact он
   * отправил бы ВТОРУЮ такую же реакцию (дубль). Здесь поиск myEventId
   * идёт по карте buildThreadReactions, где тредовые цели видны.
   */
  const handleThreadReactionToggle = useCallback(
    async (eventId: string, emoji: string) => {
      const thread = room.getThread(openThreadId ?? "");
      const chips = thread ? (buildThreadReactions(thread, client.getUserId() ?? "").get(eventId) ?? []) : [];
      const chip = chips.find((c) => c.key === emoji);
      if (chip?.mine && chip.myEventId) {
        await withRateLimit(() => client.redactEvent(room.roomId, chip.myEventId!));
      } else {
        await sendReaction(client, room.roomId, eventId, emoji);
      }
    },
    [client, room, openThreadId],
  );

  const handleJumpToMessage = useCallback(
    (eventId: string) => {
      const el = document.querySelector<HTMLElement>(`[data-event-id="${eventId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Подсветка КЛАССОМ, а не подменой style.background.
        // Почему: у своего пузыря фон — градиент из CSS, и запись в
        // el.style.background стирала его насмерть (React не перерисует
        // элемент, у которого не менялись пропсы, — пузырь оставался
        // блёклым до перезагрузки). Класс задаёт рамку и снимается чисто.
        el.classList.add("is-highlight");
        setTimeout(() => el.classList.remove("is-highlight"), 1500);
      }
    },
    [],
  );

  // Matrix links are deliberately routed through App's existing capture
  // handler, so mentions use the same DM creation path as ordinary matrix.to
  // links (no second Matrix-client flow is introduced here).
  const openMentionDm = useCallback((userId: string) => {
    const link = document.createElement("a");
    link.href = `https://matrix.to/#/${userId}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);
  const openMentionProfile = useCallback((userId: string) => {
    window.open(`https://matrix.to/#/${userId}`, "_blank", "noopener,noreferrer");
  }, []);

  /** Открыть тред по событию. Если треда ещё нет — создаём его первым ответом. */
  const handleOpenThread = useCallback(
    (eventId: string) => {
      setOpenThreadId(eventId);
    },
    [],
  );

  /** Событие из адреса не нашлось в загруженной ленте — показываем почему. */
  const [jumpMiss, setJumpMiss] = useState<string | null>(null);

  /**
   * Переход к событию из адреса (#/room/<id>/<eventId> — permalink из
   * «Поделиться», matrix.to-ссылка на сообщение). Переиспользуем
   * handleJumpToMessage из реплаев — второй прокрутчик не пишем (постановка).
   *
   * Ждём, пока лента отрисуется (items.length > 0): открытие комнаты идёт
   * через догрузку страницы истории, и раньше времени искать в DOM
   * бессмысленно. События может не быть в загруженном окне (древняя
   * история) — тогда честно говорим об этом плашкой, а не молчим.
   * Контекстную подгрузку вокруг события (GET /context) здесь сознательно
   * НЕ делаем: это этап 6 плана, отдельный режим ленты.
   */
  useEffect(() => {
    if (!scrollToEventId) return;
    if (items.length === 0) return; // лента ещё не собралась — ждём
    const found = items.some((i) => i.eventId === scrollToEventId);
    if (found) {
      // Прокрутку откладываем на кадр: DOM-узел появляется после этого
      // же рендера, и scrollIntoView до отрисовки промахнётся.
      const id = requestAnimationFrame(() => handleJumpToMessage(scrollToEventId));
      setJumpMiss(null);
      onScrollToEventHandled?.();
      return () => cancelAnimationFrame(id);
    }
    // Даём шанс догрузке: если вверху есть история, пользователь мог
    // прийти по ссылке на старое сообщение — одна страница вверх может
    // его содержать. Автоматически НЕ листаем: это увело бы человека
    // от свежих сообщений без его действия. Плашка честнее.
    setJumpMiss(scrollToEventId);
    onScrollToEventHandled?.();
  }, [scrollToEventId, items, handleJumpToMessage, onScrollToEventHandled]);

  /**
   * Догнал: событие, которое не нашлось при открытии ссылки, появилось
   * после ручной подгрузки истории (человек прокрутил вверх по подсказке
   * плашки). Подсвечиваем его и гасим плашку — обещание «оно появится
   * при подгрузке» выполнено буквально.
   */
  useEffect(() => {
    if (!jumpMiss) return;
    if (!items.some((i) => i.eventId === jumpMiss)) return;
    const id = requestAnimationFrame(() => handleJumpToMessage(jumpMiss));
    setJumpMiss(null);
    return () => cancelAnimationFrame(id);
  }, [jumpMiss, items, handleJumpToMessage]);

  // Смена комнаты — плашка промаха от предыдущей комнаты неуместна.
  useEffect(() => {
    setJumpMiss(null);
  }, [room.roomId]);

  /**
   * Кнопка «@» — переход к непрочитанным упоминаниям (Антон 12.10 15:13).
   *
   * Снимок берётся В РЕНДЕРЕ при смене комнаты — до эффектов, т.е. до того,
   * как открытие чата отправит отметку прочтения и погасит счётчик. Дальше
   * кнопка живёт по снимку (граница прочитанного + число highlight), а не по
   * текущему счётчику. Сама лента никуда не прыгает — только по нажатию.
   */
  const mentionSnapRef = useRef<{ roomId: string; readUpTo: string | null; count: number } | null>(null);
  if (mentionSnapRef.current?.roomId !== room.roomId) {
    const me = client.getUserId() ?? "";
    mentionSnapRef.current = {
      roomId: room.roomId,
      readUpTo: me ? room.getEventReadUpTo(me) : null,
      count: encrypted || invite ? 0 : (room.getUnreadNotificationCount(NotificationCountType.Highlight) ?? 0),
    };
  }
  const mentionSnap = mentionSnapRef.current;
  const [mentionVisited, setMentionVisited] = useState<string[]>([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [pendingMentionJump, setPendingMentionJump] = useState<string | null>(null);
  useEffect(() => {
    setMentionVisited([]);
    setPendingMentionJump(null);
  }, [room.roomId]);

  /** Упоминания после границы прочитанного в загруженной ленте; boundaryLoaded — граница найдена. */
  const collectMentions = useCallback((): { ids: string[]; boundaryLoaded: boolean } => {
    const me = client.getUserId() ?? "";
    const shown = new Set(itemsRef.current.map((i) => i.eventId));
    const events = room.getLiveTimeline().getEvents();
    const readUpTo = mentionSnap.readUpTo;
    let start = 0;
    let boundaryLoaded = !readUpTo;
    if (readUpTo) {
      const idx = events.findIndex((ev) => ev.getId() === readUpTo);
      if (idx >= 0) {
        start = idx + 1;
        boundaryLoaded = true;
      }
    }
    const ids: string[] = [];
    for (const ev of events.slice(start)) {
      const id = ev.getId();
      if (!id || !shown.has(id) || ev.getSender() === me) continue;
      const mentions = ev.getContent()?.["m.mentions"] as { user_ids?: unknown; room?: unknown } | undefined;
      const byMentions =
        (Array.isArray(mentions?.user_ids) && mentions.user_ids.includes(me)) || mentions?.room === true;
      const byPush = client.getPushActionsForEvent(ev)?.tweaks?.highlight === true;
      if (byMentions || byPush) ids.push(id);
    }
    return { ids, boundaryLoaded };
  }, [client, room, mentionSnap]);

  const mentionState = (() => {
    if (mentionSnap.count <= 0) return { remaining: 0, next: null as string | null };
    const { ids, boundaryLoaded } = collectMentions();
    const left = ids.filter((id) => !mentionVisited.includes(id));
    const remaining = boundaryLoaded || !older
      ? left.length
      : Math.max(left.length, mentionSnap.count - mentionVisited.length);
    return { remaining, next: left[0] ?? null };
  })();

  const onMentionJump = useCallback(() => {
    if (mentionLoading) return;
    (async () => {
      // Граница прочитанного не загружена — более ранние упоминания могут
      // быть выше. Догружаем историю страницами (как подгрузка вверх), пока
      // граница не появится или история не кончится.
      let found = collectMentions();
      if (!found.boundaryLoaded && canLoadOlder(room)) {
        setMentionLoading(true);
        try {
          for (let i = 0; i < 20 && !found.boundaryLoaded && canLoadOlder(room); i++) {
            await loadOlder(client, room, 40);
            refresh();
            itemsRef.current = buildTimeline(room, client.getUserId() ?? "");
            found = collectMentions();
          }
        } catch (e) {
          setHistoryError((e as Error).message);
        } finally {
          setMentionLoading(false);
        }
      }
      const next = found.ids.find((id) => !mentionVisited.includes(id));
      if (!next) {
        // Упоминаний в ленте не нашлось (например, они в треде) — гасим кнопку.
        setMentionVisited((prev) => [...prev, ...Array(Math.max(0, mentionSnap.count - prev.length)).fill("")]);
        return;
      }
      setMentionVisited((prev) => [...prev, next]);
      setPendingMentionJump(next);
    })();
  }, [mentionLoading, collectMentions, room, client, refresh, mentionVisited, mentionSnap]);

  // Прыжок после отрисовки: узел события появляется в DOM после рендера.
  useEffect(() => {
    if (!pendingMentionJump) return;
    if (!items.some((i) => i.eventId === pendingMentionJump)) return;
    const id = requestAnimationFrame(() => handleJumpToMessage(pendingMentionJump));
    setPendingMentionJump(null);
    return () => cancelAnimationFrame(id);
  }, [pendingMentionJump, items, handleJumpToMessage]);

  const sendWithMode = useCallback(
    async (segments: RichSegment[]) => {
      if (!perms.canSendMessage) {
        throw new Error(t("ui.nedostatochno_prav_dlya_otpravki_soobscheniy"));
      }
      // Правка и ответ принимают сегменты напрямую: упоминания внутри них
      // обязаны пушить (патч Антона 2026-09-10 10:24), а сведение к плоскому
      // тексту молча теряло и push, и formatted_body.
      if (editing) {
        const sourceEvent = room.getLiveTimeline().getEvents().find((ev) => ev.getId() === editing.eventId);
        if (!sourceEvent) {
          throw new Error(t("ui.redaktiruemoe_soobschenie_ne_naydeno_v_len"));
        }
        await editMessage(client, room.roomId, sourceEvent, segments);
        return;
      }
      if (replyTo) {
        // Находим исходное событие для формирования fallback-цитаты.
        const sourceEvent = room.getLiveTimeline().getEvents().find((ev) => ev.getId() === replyTo.eventId);
        if (!sourceEvent) {
          throw new Error(t("ui.soobschenie_na_kotoroe_vy_otvechaete_ne_nay"));
        }
        await sendReply(client, room.roomId, sourceEvent, segments);
        markOwnMessageSent();
        return;
      }
      await sendTextMessage(client, room.roomId, segments);
      markOwnMessageSent();
    },
    [client, room, perms.canSendMessage, editing, replyTo, markOwnMessageSent],
  );

  // Присутствие в звонке комнаты (MatrixRTC, MSC4143). Плашка «в комнате
  // идёт звонок · N участников» — шаг 1. Шаг 2: реальный вход со звуком
  // по клику (useCallJoin в rtc.ts).
  const callPresence = useCallPresence(client, room);
  // Звонок приходит СВЕРХУ из App (критичный баг 2026-09-12: свой хук
  // здесь умирал при смене комнаты и обрывал звонок). Заглушка сохраняет
  // прежнюю форму объекта, чтобы весь JSX ниже не пришлось обвешивать
  // проверками на null: с заглушкой кнопки просто disabled, а плашка
  // живёт по callPresence, как и раньше.
  const callJoin: CallJoin = callJoinProp ?? {
    roomEncrypted: encrypted,
    state: "idle",
    error: null,
    joined: false,
    participants: 0,
    join: () => {},
    leave: () => {},
    soloSecondsLeft: null,
    declinedBy: null,
    isVideo: false,
    cameraOn: false,
    toggleCamera: () => {},
    // Добавление камеры в идущий аудиозвонок (12.09): в заглушке
    // состояние покоя — ничего не запрашивается, ошибки нет.
    cameraBusy: false,
    cameraError: null,
    cameraInitialized: false,
    micOn: true,
    toggleMic: () => {},
    // Screen share (12.09 12:28): в заглушке состояние покоя.
    screenOn: false,
    screenBusy: false,
    screenError: null,
    toggleScreenShare: () => {},
    videoTiles: [],
    callParticipants: [],
    // Выбор устройств (13.09): в заглушке списки пусты и выбирать
    // нечего — звонка нет. Меню-стрелка просто не покажет пунктов.
    devices: { mics: [], cameras: [], speakers: [] },
    activeDevices: { mic: "", camera: "", speaker: "" },
    selectDevice: () => {},
    deviceNotice: null,
    dismissDeviceNotice: () => {},
    diagnosticsAvailable: false,
    copyDiagnostics: async () => {},
  };

  return (
    <>
    <section style={s.main}>
      <header
        className="alg-room-header"
        style={{ ...s.roomHeader, display: "flex", alignItems: "center", gap: space.lg }}
      >
        {/* МОБИЛЬНАЯ КНОПКА «НАЗАД» (спека 2026-09-20, этап 2): стрелка в
            шапке чата ведёт к списку. Рендерим только когда обработчик
            передан (App передаёт его только на ≤640px); на десктопе
            список всегда виден, и кнопка была бы лишней. */}
        {onMobileBack && (
          <button
            type="button"
            className="alg-mobile-back"
            onClick={onMobileBack}
            data-testid="mobile-back"
            aria-label={t("app.goBack")}
            title={t("app.goBack")}
          >
            <IconChevronLeft size={24} />
          </button>
        )}
        {/* ВСЯ зона «аватар + название + участники» — одна кликабельная
            кнопка, открывающая страницу комнаты (как в Telegram). Пять
            текстовых кнопок действий отсюда убраны: они переехали на саму
            страницу по секциям. Эмодзи ⚙️ убран совсем. */}
        <button
          type="button"
          onClick={() => setShowRoomPage(true)}
          data-testid="header-open-room-page"
          title={t("room.headerOpenPage")}
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: space.lg,
            padding: 0,
            border: "none",
            background: "transparent",
            font: "inherit",
            color: color.text,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <div
            className="alg-room-header-avatar"
            style={{
              width: 40,
              height: 40,
              borderRadius: radius.circle,
              flex: "0 0 40px",
              display: "grid",
              placeItems: "center",
              background: avatar.url ? "transparent" : colorOf(room.roomId),
              color: color.textOnAccent,
              fontSize: 14,
              fontWeight: 700,
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            {avatar.url ? (
              <img
                src={avatar.url}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              (room.name || room.roomId).replace(/^[#!@]/, "").slice(0, 2).toUpperCase()
            )}
          </div>
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }} data-testid="room-title">
              {encrypted && "🔒 "}
              {pinned && <span title={t("room.favourite")}>★ </span>}
              {room.name || room.roomId}
            </div>
            <div className="alg-room-header-subtitle" style={{ fontSize: 12, color: palette.textDim }}>
              {room.getJoinedMemberCount()}{t("ui.uchastnikov")}</div>
          </div>
        </button>

        {/* Справа — максимум две компактные иконки: уведомления этой
            комнаты (тоже ведёт на страницу, к её секции) и «ещё». Кнопки
            поиска НЕТ сознательно: поиск по сообщениям — отдельный пункт
            очереди, заглушку-кнопку не ставим. */}
        {invite ? (
          <div className="alg-room-header-actions" style={{ ...s.actionRow, flex: "0 0 auto", flexWrap: "nowrap" }}>
            <button
              type="button"
              style={{ ...s.actionButton, background: color.ok, color: color.textOnAccent, border: "none" }}
              onClick={onAcceptInvite}
              data-testid="header-accept"
            >{t("common.accept")}</button>
            <button
              type="button"
              style={s.actionButton}
              onClick={onDeclineInvite}
              data-testid="header-decline"
            >{t("common.decline")}</button>
          </div>
        ) : (
          <div className="alg-room-header-actions" style={{ ...s.actionRow, flex: "0 0 auto", flexWrap: "nowrap" }}>
            {/* НАЧАТЬ ЗВОНОК (Антон 10.09 18:51: «нет плашки звонка нигде»).

                Шаг 2 дал вход в звонок, но плашка появлялась только когда звонок
                УЖЕ идёт. Начать его было НЕЧЕМ: в пустой комнате плашки нет,
                а без плашки нет кнопки — замкнутый круг, звонок мог только
                начать кто-то из другого клиента. Кнопка в шапке видна всегда
                и разрывает его. Она же — тот самый клик, под который браузер
                отдаёт микрофон. */}
            <button
              type="button"
              onClick={() => (callJoin.joined ? callJoin.leave() : callJoin.join())}
              /* E2EE: в зашифрованной комнате звонок не работает (crypto
                 выключен). Кнопку приглушаем ЗАРАНЕЕ, чтобы человек не
                 нажимал вхолостую. Подсказка при наведении объясняет почему. */
              disabled={callJoinProp === null || callJoin.state === "joining" || encrypted}
              style={{
                ...s.iconButton,
                ...(callJoin.state === "in-call"
                  ? { background: color.danger, color: color.textOnAccent }
                  : null),
                ...(encrypted ? { opacity: 0.45, cursor: "not-allowed" } : null),
              }}
              data-testid="header-call"
              data-call-state={callJoin.state}
              title={
                encrypted
                  ? t("ui.zvonki_v_zashifrovannyh_komnatah_poka_ne_2")
                  : callJoin.state === "in-call"
                    ? t("room.headerCallLeave")
                    : callJoin.state === "joining"
                      ? t("room.callConnecting")
                      : callPresence.active
                        ? t("room.headerCallJoin")
                        : t("room.headerCallStart")
              }
            >
              {callJoin.state === "joining" ? (
                "⋯"
              ) : callJoin.state === "in-call" ? (
                <IconPhoneOff size={18} />
              ) : (
                <IconPhone size={18} />
              )}
            </button>
            {/* STEP6 (Антон 14:19: «просто, отдельной кнопкой»): 📹 —
                ЕДИНСТВЕННЫЙ вход в видеозвонок. Никакого переключения
                камеры в идущем аудиозвонке, никакого выбора устройств:
                нажал 📹 — звонок с камерой. Аудиокнопка 📞 выше НЕ
                меняется: видео — опция, аудиопуть не должен деградировать.
                В уже идущем звонке кнопка видна, но вход заново не нужен —
                она тогда просто disabled (второй join запрещён в rtc.ts). */}
            <button
              type="button"
              onClick={() => callJoin.join(true)}
              /* E2EE: в зашифрованной комнате видеозвонок тоже не работает
                 (crypto выключен). Кнопку приглушаем ЗАРАНЕЕ, чтобы человек
                 не нажимал вхолостую. Подсказка при наведении объясняет почему. */
              disabled={callJoinProp === null || callJoin.state !== "idle" || encrypted}
              style={{
                ...s.iconButton,
                ...(encrypted ? { opacity: 0.45, cursor: "not-allowed" } : null),
              }}
              data-testid="header-video-call"
              title={
                encrypted
                  ? t("ui.zvonki_v_zashifrovannyh_komnatah_poka_ne_2")
                  : callJoin.state !== "idle"
                    ? t("room.callAlreadyIn")
                    : callPresence.active
                      ? t("room.headerVideoJoin")
                      : t("room.headerVideoStart")
              }
            >
              {/* Эмодзи 📹 заменён на SVG-камеру (STEP8 эт.1, постановка
                  Антона: «камера = нормальная иконка вместо 📹»). Эмодзи
                  рисуется цветным пятном и на каждой платформе свой —
                  иконка везде одна и та же. */}
              <IconVideo size={18} />
            </button>
            {/* КОЛОКОЛЬЧИК УБРАН (Антон 18:58: «убери колокольчик отсюда,
                он не нужен, он просто дублируется везде, потому что у нас
                есть в настройках комнаты»). Управление уведомлениями
                комнаты живёт на странице комнаты (RoomPage -> секция
                «Уведомления»), глобальные настройки будут в профиле.
                Кнопка в шапке открывала ТУ ЖЕ страницу комнаты, что и
                «⋯» справа — то есть была третьим входом в одно место. */}
            <button
              type="button"
              style={s.iconButton}
              onClick={() => setShowRoomPage(true)}
              data-testid="header-more"
              title={t("room.pageLabel")}
            >
              <IconMore size={18} />
            </button>
          </div>
        )}
      </header>

      {invite ? (
        <>
          <div style={s.notice} data-testid="invite-notice">{t("room.thisIsWord")}<b>{t("room.invitationWord")}</b>{t("room.inviteHintTail")}
          </div>
          <div style={{ flex: "1 1 auto" }} />
        </>
      ) : encrypted ? (
        <>
          {/* Плашка вместо пустой ленты. Так решено: E2EE в клиенте выключен
              намеренно (crypto не инициализируется), а m.room.encryption в
              комнате необратим — значит честно говорим, почему пусто. */}
          <div style={s.notice} data-testid="encrypted-notice">
            <b>{t("room.encryptedNotice")}</b>
            <br />
            {t("room.encryptedNoticeTail")}
          </div>
          <div style={{ flex: "1 1 auto" }} />
        </>
      ) : (
        <>
          {/* Плашка видна, пока есть хоть одно из: чужой звонок в комнате,
              мы входим (joining), мы в звонке, ошибка входа. Без joining/
              error плашка мигала бы: в одиночной комнате до публикации
              членства присутствие ещё false, и человек не видел бы
              «Подключаемся…».

              ШАГ 4 (Антон 2026-09-11 13:03, критичный): активный звонок
              теперь живёт КАРТОЧКОЙ В ЛЕНТЕ внизу, и плашка сверху ему
              дублирует — на планшете в альбомной ориентации верхнюю плашку
              вообще не видно. Когда в ленте есть АКТИВНАЯ карточка, плашку
              рисуем ТОЛЬКО для состояний, которых карточка показать не
              может: входим сами (joining), мы в звонке, ошибка входа.
              Чужой активный звонок (idle) покрывает карточка — её кнопка
              и есть «Присоединиться». Завершённая карточка плашку НЕ
              гасит: звонок мог начаться заново вне загруженной истории. */}
          {(callPresence.active || callJoin.state !== "idle") &&
            !(items.some((i) => i.call?.active) && callJoin.state === "idle") && (
            <CallBanner
              participants={callJoin.joined ? callJoin.participants : callPresence.participants}
              state={callJoin.state}
              error={callJoin.error}
              /* Отвечаем тем же, чем нам звонят: на видеозвонок — с камерой
                 (Антон 16:06). Аудиозвонок остаётся аудио. */
              onJoin={() => callJoin.join(activeCallIsVideo(room))}
              onDecline={
                callPresence.active && !callJoin.joined
                  ? () => void declineActiveCall(client, room)
                  : undefined
              }
              onLeave={callJoin.leave}
              soloSecondsLeft={callJoin.soloSecondsLeft}
              declinedBy={callJoin.declinedBy}
            />
          )}
          {/* STEP8 ЭТАП 4: ЭКРАН ЗВОНКА в стиле Teams (Антон 11.09 16:20).

              БЫЛО: прямоугольник ВНУТРИ ленты — сетка minmax(160px,1fr),
              подпись мелочью в углу плитки, под сеткой текстовые кнопки
              «Камера вкл»/«Микрофон выкл». СТАЛО: звонок занимает ВСЁ
              ОКНО ПРИЛОЖЕНИЯ (не браузера — F11/requestFullscreen под
              прямым запретом Антона и не используются).

              Устройство:
              - CallScreen — абсолютный слой поверх ленты и композера в
                пределах окна чата (s.main уже position:relative, слой
                НЕ перекрывает список комнат слева: в Teams список чатов
                тоже остаётся виден, и из звонка можно перейти в другую
                комнату — вид на всю область контента меняется сам);
              - в 1:1 чужое видео/экран заполняет медиазону, своё превью
                плавает малым PiP поверх и не меняет размер чужого видео;
              - при 3+ участниках — адаптивная сетка; говорящий получает
                округлую красную рамку из LiveKit speaker events;
              - контролы как в Teams: круглые кнопки рядом по центру
                снизу поверх видео, без текста; выключенное состояние —
                перечёркнутая иконка на приглушённом фоне; «завершить»
                красная.

              ЭТАП 5 (PiP): структура уже разделяет «сетка всех» и
              «один говорящий» — CallScreen примет список плиток от
              будущего выбора активного спикера, сама смена режима по
              потере фокуса окна здесь НЕ делается (запрет этапа). */}
          {callJoin.state === "in-call" && !callMinimized && (
            <CallScreen
              client={client}
              tiles={callJoin.videoTiles}
              callParticipants={callJoin.callParticipants}
              cameraOn={callJoin.cameraOn}
              cameraBusy={callJoin.cameraBusy}
              cameraError={callJoin.cameraError}
              cameraInitialized={callJoin.cameraInitialized}
              micOn={callJoin.micOn}
              onToggleCamera={callJoin.toggleCamera}
              onToggleMic={callJoin.toggleMic}
              screenOn={callJoin.screenOn}
              screenBusy={callJoin.screenBusy}
              screenError={callJoin.screenError}
              onToggleScreenShare={callJoin.toggleScreenShare}
              onLeave={callJoin.leave}
              soloSecondsLeft={callJoin.soloSecondsLeft}
              participants={callJoin.participants}
              devices={callJoin.devices}
              activeDevices={callJoin.activeDevices}
              onSelectDevice={callJoin.selectDevice}
              deviceNotice={callJoin.deviceNotice}
              onDismissDeviceNotice={callJoin.dismissDeviceNotice}
              onCopyDiagnostics={callJoin.copyDiagnostics}
              /* Титры поверх звонка (Антон 2026-09-12 08:34). Источник
                 титров — ГОТОВАЯ лента items этой же комнаты: отдельной
                 подписки на timeline у титров НЕТ (постановка п.3 — своя
                 подписка = дубли и старые события). Компонент монтируется
                 ВМЕСТЕ с экраном звонка, и момент монтирования = момент
                 входа: всё, что было в ленте до него, в титры не попадает.

                 ДОПОЛНЕНИЕ 09:20: свёрнутый звонок (callMinimized) экран
                 НЕ рисует вообще — открываются лента и композер, а App
                 показывает мини-фрейм. Это смена РЕЖИМА ПОКАЗА: объект
                 звонка (videoTiles, медиа) живёт в держателе App и при
                 сворачивании не пересоздаётся — видео НЕ переподключается. */
              captionItems={items}
              onCompose={onMinimizeCall}
              /* "Add participant" in a call (Anton 2026-09-17 08:18): group
                 rooms only — a DM stays a DM (Anton 08:19). Opens the room's
                 usual invite dialog; invited people are auto-added, no
                 confirmation step, same as everywhere else. */
              onAddParticipant={
                directRoomIds(client).has(room.roomId)
                  ? onAddParticipantFromDm
                  : canInviteHere
                    ? onInvite
                    : undefined
              }
            />
          )}
          {/* После ошибки, разрыва или обычного выхода журнал остаётся
              доступен в памяти этого звонка: Антону не нужен Web Inspector,
              а кнопка не исчезает вместе с полноэкранным слоем. */}
          {callJoin.state !== "in-call" && callJoin.diagnosticsAvailable && (
            <div
              data-testid="call-diagnostics-after"
              style={{ padding: `${space.sm}px ${space.lg}px`, display: "flex", justifyContent: "flex-end" }}
            >
              <CallDiagnosticsButton onCopy={callJoin.copyDiagnostics} />
            </div>
          )}
          {historyError && (
            <div style={{ padding: "6px 16px", fontSize: 12, color: palette.danger }}>
              {tfmt("room.historyError", { error: historyError })}
            </div>
          )}
          {receiptError && (
            <div
              style={{ padding: "6px 16px", fontSize: 12, color: palette.danger }}
              data-testid="receipt-error"
            >
              {tfmt("room.receiptError", { error: receiptError })}
            </div>
          )}
          {jumpMiss && (
            <div
              style={{ padding: "6px 16px", fontSize: 12, color: palette.textDim }}
              data-testid="jump-miss"
            >
              {t("room.messageLinkMiss")}{" "}
              <button
                type="button"
                style={{ font: "inherit", border: "none", background: "none", color: palette.accent, cursor: "pointer", padding: 0 }}
                onClick={() => setJumpMiss(null)}
              >{t("app.hideActionError")}</button>
            </div>
          )}
          <Timeline
            key={room.roomId}
            client={client}
            room={room}
            items={items}
            reactions={reactions}
            roomId={room.roomId}
            canLoadOlder={older}
            loadingOlder={loadingOlder}
            onLoadOlder={onLoadOlder}
            onUnreadReviewedThrough={setReviewedThroughEventId}
            scrollToBottomSignal={scrollToBottomSignal}
            onMessagePrivately={openMentionDm}
            onViewProfile={openMentionProfile}
            onReply={handleReply}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReactionToggle={handleReactionToggle}
            onOpenThread={handleOpenThread}
            onJumpToMessage={handleJumpToMessage}
            onJumpToMention={mentionState.remaining > 0 ? onMentionJump : undefined}
            jumpToMentionBusy={mentionLoading}
            callJoinState={{ state: callJoin.state, error: callJoin.error }}
            onCallJoin={() => callJoin.join(activeCallIsVideo(room))}
            onCallDecline={() => void declineActiveCall(client, room)}
            onCallLeave={callJoin.leave}
          />
          {/* «X печатает…» — НАД композером (Антон, 2026-09-16 21:27 UTC).
              Источник — ephemeral m.typing из sync (см. matrix/typing.ts). */}
          <TypingIndicator client={client} room={room} />
          <Composer
            disabled={!perms.canSendMessage}
            roomId={room.roomId}
            members={membersOf(client, room)}
            canMentionRoom={perms.canMentionRoom}
            onSend={sendWithMode}
            onSendFile={sendFile}
            onSendVoice={sendVoice}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            editing={editing}
            onCancelEdit={() => setEditing(null)}
          />
        </>
      )}
    </section>

    {openThreadId && (
      <ThreadPanel
        client={client}
        room={room}
        rootId={openThreadId}
        // getThread возвращает null для сообщения БЕЗ ответов, то есть
        // всегда при создании нового треда. Бросать здесь исключение
        // (как было) означало белый экран на всё приложение: рендер
        // падал и React размонтировал дерево вместе с выходом.
        thread={room.getThread(openThreadId)}
        onClose={() => setOpenThreadId(null)}
        onReply={handleReply}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onReactionToggle={handleThreadReactionToggle}
      />
    )}

    {showRoomPage && (
      <RoomPage
        client={client}
        room={room}
        canInvite={canInviteHere}
        onInvite={onInvite}
        onRoomLink={onRoomLink}
        onLeave={onLeave}
        onToggleFavourite={onToggleFavourite}
        onClose={() => setShowRoomPage(false)}
      />
    )}

    {deleteTarget && (
      <DeleteMessageDialog
        client={client}
        room={room}
        eventId={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    )}
    </>
  );
}

/**
 * Экран звонка на всё окно приложения (STEP8 этап 4, стиль Teams).
 *
 * ПОЧЕМУ АБСОЛЮТНЫЙ СЛОЙ, А НЕ ЗАМЕНА ЛЕНТЫ: лента и композер остаются
 * смонтированными под слоем — их состояние (позиция прокрутки, черновик,
 * режим ответа) не теряется при входе/выходе из звонка. Слой живёт внутри
 * <section style={s.main}> (там position:relative), поэтому накрывает
 * ровно окно чата: шапку, ленту, композер — и НЕ накрывает список комнат.
 * Список оставлен видимым сознательно (решение этапа): в Teams левая
 * колонка в звонке тоже видна, и человек может уйти в другую комнату,
 * не выходя из звонка. Антон спрашивал только про «полное окно
 * приложения» против фуллскрина браузера — окно чата + видимый список
 * это и есть полное окно приложения, а не его часть.
 *
 * F11 / requestFullscreen НЕ используются — прямой запрет Антона 16:20.
 *
 * ГОВОРЯЩИЙ: rtc.ts получает состояние только из LiveKit
 * ActiveSpeakersChanged и очищает его по mute/disconnect.
 */

type CallTile = {
  key: string;
  stream: MediaStream;
  local: boolean;
  /** Подпись (displayname чужого участника, «Вы» для своего). */
  label: string;
  participantIdentity: string;
  /** Говорит ли участник сейчас по данным LiveKit. */
  speaking: boolean;
  /** Микрофон участника выключен (LiveKit). */
  micMuted?: boolean;
  /** Камера выключена (LiveKit): вместо чёрного видео — заглушка. */
  cameraOff?: boolean;
  /**
   * Камера или демонстрация экрана (screen share 12:28). У экрана —
   * object-fit: contain (постановка дословно): содержимое чужого
   * рабочего стола нельзя обрезать, как лицо камеры. undefined =
   * «camera» (старые плитки правки не требуют).
   */
  kind?: "camera" | "screen";
};

/**
 * Кнопка «Перевернуть камеру» нужна только на телефоне/планшете (фронт/тыл).
 * На ноутбуке (Mac-обёртка WKWebView, браузер) задней камеры нет, а вторая
 * камера — это внешняя веб-камера, она выбирается в меню камеры (Антон 12.10).
 * Признак: Capacitor-приложение (Android) ИЛИ основной указатель «грубый»
 * (тач-телефон в мобильном браузере). maxTouchPoints не берём: он > 0 и
 * у ноутбуков с сенсорным экраном.
 */
function isTouchCallPlatform(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.() === true) return true;
  } catch {
    /* нет Capacitor — проверяем указатель */
  }
  return typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

/**
 * Flip камеры — ТОЛЬКО в нативном мобильном приложении (Android, Capacitor).
 * Антон 12.10 18:50: «Flip должен оставаться у Android и iOS и не должен
 * присутствовать на Mac и в вебе». Веб = любой браузер, включая мобильный,
 * поэтому признак `pointer: coarse` здесь НЕ используется (в отличие от
 * подписей кнопок — там тач-браузер по-прежнему считается тачем).
 * iOS — отдельное нативное приложение со своим Flip (CallView.swift).
 */
function canFlipCameraPlatform(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    return cap?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

function CallScreen({
  client,
  tiles: tilesAll,
  callParticipants,
  cameraOn,
  micOn,
  onToggleCamera,
  cameraBusy,
  cameraError,
  cameraInitialized,
  onToggleMic,
  screenOn,
  screenBusy,
  screenError,
  onToggleScreenShare,
  onLeave,
  soloSecondsLeft,
  participants,
  captionItems,
  onCompose,
  devices,
  activeDevices,
  onSelectDevice,
  deviceNotice,
  onDismissDeviceNotice,
  onCopyDiagnostics,
  onAddParticipant,
}: {
  client: MatrixClient;
  tiles: CallTile[];
  /** Add a participant to the call (group rooms only). undefined = no button. */
  onAddParticipant?: () => void;
  callParticipants: CallParticipant[];
  cameraOn: boolean;
  micOn: boolean;
  onToggleCamera: () => void;
  /** Камера сейчас запрашивается (висит системный диалог доступа). */
  cameraBusy: boolean;
  /** Ошибка последнего включения камеры, показываем текстом. */
  cameraError: string | null;
  /** Камера хоть раз включалась в этом звонке — различает «выключил сам»
      (красная кнопка) и «ещё не включал» (белая перечёркнутая). */
  cameraInitialized: boolean;
  onToggleMic: () => void;
  /** Идёт ли наша демонстрация экрана (screen share 12:28). */
  screenOn: boolean;
  /** Висит системный выбор окна/экрана. */
  screenBusy: boolean;
  /** Ошибка последней попытки демонстрации — показываем текстом. */
  screenError: string | null;
  onToggleScreenShare: () => void;
  onLeave: () => void;
  /** Секунд до автовыхода, когда я позвонил и никто не ответил. */
  soloSecondsLeft?: number | null;
  /** Людей в звонке, включая меня (считается по людям, не устройствам). */
  participants: number;
  /** Лента комнаты для титров (уже собранная в RoomView — своя подписка
      титрам не нужна, см. постановку п.3). */
  captionItems: TimelineItem[];
  /** Кнопка «написать»: сворачивает звонок в мини-фрейм, открывая обычный
      композер (дополнение 2026-09-12 09:20). undefined — сворачивание
      недоступно (проп не передан), кнопка не рисуется. */
  onCompose?: () => void;
  /* ---- Выбор устройств (задача 2026-09-13, Антон 14:16) ---- */
  /** Списки устройств по трём группам (микрофоны/камеры/динамики). */
  devices: MediaDeviceLists;
  /** Активные deviceId сейчас; "" = системное по умолчанию. */
  activeDevices: { mic: string; camera: string; speaker: string };
  /** Выбор устройства из меню (переключение на лету, persist). */
  onSelectDevice: (kind: "mic" | "camera" | "speaker", deviceId: string, label?: string) => void;
  /** Уведомление про устройства (исчез выбранный и т.п.) — текстом, не молча. */
  deviceNotice: string | null;
  onDismissDeviceNotice: () => void;
  /** Копирование приватно отфильтрованного журнала звонка. */
  onCopyDiagnostics: () => Promise<void>;
}) {
  const t = useT();
  const tfmt = useTF();
  const waiting = soloSecondsLeft !== null && soloSecondsLeft !== undefined;
  const oneToOne = participants === 2;
  // Камера выключена (Антон 12.10): её плитка не рисует чёрное видео —
  // участник уходит в плитку-заглушку CallParticipantView (аватар, имя,
  // перечёркнутая камера). Экранные плитки не трогаем. Включил камеру —
  // cameraOff снова false, и видео возвращается само.
  const cameraOffIdentities = new Set(
    tilesAll.filter((tile) => tile.kind !== "screen" && tile.cameraOff).map((tile) => tile.participantIdentity),
  );
  const tiles = tilesAll.filter((tile) => !(tile.kind !== "screen" && tile.cameraOff));
  const count = tiles.length;
  const isCameraOff = (participant: CallParticipant) =>
    participant.cameraOff || cameraOffIdentities.has(participant.identity);
  const localTiles = tiles.filter((tile) => tile.local);
  const remoteTiles = tiles.filter((tile) => !tile.local);
  const participantIdentitiesWithVideo = new Set(tiles.map((tile) => tile.participantIdentity));
  const audioOnlyParticipants = callParticipants.filter(
    (participant) => !participantIdentitiesWithVideo.has(participant.identity),
  );
  const localParticipant = callParticipants.find((participant) => participant.local) ?? null;
  const remoteParticipant = callParticipants.find((participant) => !participant.local) ?? null;
  // В 1:1 удалённый screen share важнее удалённой камеры. Камера снова
  // станет главной автоматически, когда screen track отпишется.
  const primaryRemote =
    remoteTiles.find((tile) => tile.kind === "screen") ?? remoteTiles[0] ?? null;

  /* СЦЕНА ДЕМОНСТРАЦИИ ЭКРАНА (Антон 2026-10-15 10:06: «the shared screen
     should be the full one»). В групповом звонке чужая демонстрация стояла
     обычной плиткой сетки размером с аватар — разглядеть нельзя.
     Стало: если кто-то из ДРУГИХ участников показывает экран, его экран
     занимает основную область целиком (contain, без обрезки), остальные —
     в узкой полосе миниатюр сбоку (широкое окно) или снизу (узкое/портрет).
     Двое показывают — на сцене последний начавший; клик по миниатюре
     чужого экрана выносит её на сцену. Свой экран на сцену не выносим
     (зеркало собственного рабочего стола) — только в полосу. Демонстрация
     закончилась — screen-плиток нет, возвращается обычная сетка. */
  const screenSeqRef = useRef<{ next: number; seen: Map<string, number> }>({ next: 0, seen: new Map() });
  const remoteScreenTiles = remoteTiles.filter((tile) => tile.kind === "screen");
  {
    const reg = screenSeqRef.current;
    const live = new Set(remoteScreenTiles.map((tile) => tile.key));
    for (const key of [...reg.seen.keys()]) if (!live.has(key)) reg.seen.delete(key);
    for (const tile of remoteScreenTiles) if (!reg.seen.has(tile.key)) reg.seen.set(tile.key, reg.next++);
  }
  const [pinnedScreenKey, setPinnedScreenKey] = useState<string | null>(null);
  const latestRemoteScreen = remoteScreenTiles.reduce<CallTile | null>((best, tile) => {
    const seen = screenSeqRef.current.seen;
    return !best || (seen.get(tile.key) ?? 0) > (seen.get(best.key) ?? 0) ? tile : best;
  }, null);
  // Новая демонстрация, начатая ПОСЛЕ ручного выбора, снова побеждает:
  // «на сцене последний начавший». Ручной выбор действует, пока не появится
  // более свежий экран или пока выбранный экран не исчезнет.
  const pinnedTile = remoteScreenTiles.find((tile) => tile.key === pinnedScreenKey) ?? null;
  const pinnedSeq = pinnedScreenKey ? screenSeqRef.current.seen.get(pinnedScreenKey) : undefined;
  const pinnedAt = useRef<{ key: string | null; maxSeqWhenPinned: number }>({ key: null, maxSeqWhenPinned: -1 });
  const latestSeq = latestRemoteScreen ? screenSeqRef.current.seen.get(latestRemoteScreen.key) ?? -1 : -1;
  const stageTile: CallTile | null =
    oneToOne || !latestRemoteScreen
      ? null
      : pinnedTile && pinnedSeq !== undefined && pinnedAt.current.key === pinnedScreenKey && latestSeq <= pinnedAt.current.maxSeqWhenPinned
        ? pinnedTile
        : latestRemoteScreen;
  const pinScreen = (key: string) => {
    pinnedAt.current = { key, maxSeqWhenPinned: latestSeq };
    setPinnedScreenKey(key);
  };
  const stripTiles = stageTile ? tiles.filter((tile) => tile.key !== stageTile.key) : [];

  /**
   * Поддержка getDisplayMedia (задача 2026-09-13, правило Антона 12.09
   * 10:24: приглушённая кнопка = ТОЛЬКО «нажать нельзя»). Браузер без
   * getDisplayMedia (старый Safari, не-secure context) — единственный
   * случай, когда кнопку демонстрации глушим: нажать её осмысленно
   * нельзя, и об этом надо сказать в подписи ДО клика, а не ошибкой
   * после. Проверка синхронная, без эффекта: API либо есть, либо его
   * не появится в жизни этой страницы.
   */
  const screenSupported =
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getDisplayMedia === "function";

  /* СЕТКА ПЛАШЕК МАСШТАБИРУЕТСЯ ПОД ЧИСЛО УЧАСТНИКОВ И РАЗМЕР ОКНА
     (Антон 2026-09-18, п.7: «сейчас 7 маленьких плиток в углу и пустая сцена»).

     Почему было пусто. Сетка стояла на ФИКСИРОВАННОМ
     `repeat(auto-fit, minmax(min(100%, 240px), 1fr))`. auto-fit набивает в строку
     столько колонок по 240px, сколько влезет ПО ШИРИНЕ, и ВОВСЕ НЕ СМОТРИТ
     на высоту. На широком экране 7 участников выстраивались в одну-две
     полоски высотой в aspect-ratio 16/9 от 240px (≈135px) и прижимались к центру,
     оставляя под собой пустое поле: плиткам не разрешалось стать больше просто
     потому, что минимум был жёстким, а число колонок — следствием только ширины.

     Стало. Число колонок считаем САМИ по числу плашек и ФАКТИЧЕСКИМ размерам
     медиазоны (ResizeObserver): перебираем варианты 1..N колонок и берём тот,
     где плитка 16/9 получается САМОЙ КРУПНОЙ и все ряды влезают по высоте.
     Это тот же принцип, что в Teams/Meet: сначала раскладка, потом размер.
     Пока все влезают — прокрутки нет и пустого места не остаётся; когда
     участников столько, что плитка упирается в читаемый минимум (120px),
     сетка честно уходит в прокрутку, а не растворяет людей в пиксели.
     Размер окна учтён автоматически: ResizeObserver следит за медиазоной,
     а не за window — боковая панель и список чатов тоже меняют её ширину. */
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [gridBox, setGridBox] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  useEffect(() => {
    const el = gridRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry.contentRect;
      setGridBox((prev) =>
        Math.abs(prev.w - box.width) < 1 && Math.abs(prev.h - box.height) < 1
          ? prev
          : { w: box.width, h: box.height },
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [oneToOne]);

  /* ЗАЗОРЫ СЦЕНЫ ДЕМОНСТРАЦИИ (Антон 2026-10-15 12:00, «Исправить тоже»).
     Панель кнопок звонка плавает абсолютно поверх низа медиазоны, кнопка
     «Copy call diagnostics» — поверх правого верхнего угла. В обычной
     сетке это терпимо, но на сцене демонстрации панель закрывала низ
     экрана и подпись «Screen: …», а кнопка — верхнюю миниатюру полосы.
     Меряем ФАКТИЧЕСКИЕ высоты панели и кнопки (ResizeObserver: подписи,
     перенос, свёрнутое меню устройств меняют их) и в режиме сцены
     резервируем: снизу — всё, что панель выступает над штатным отступом
     контейнера (80px), сверху у полосы — высоту кнопки. Отступ задаём
     margin'ом, а не padding'ом: мобильный CSS обнуляет padding сетки. */
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const diagRef = useRef<HTMLDivElement | null>(null);
  const [controlsH, setControlsH] = useState(0);
  const [diagH, setDiagH] = useState(0);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const watch = (el: HTMLElement | null, set: (h: number) => void) => {
      if (!el) return null;
      const update = () => {
        const h = Math.ceil(el.getBoundingClientRect().height);
        set(h);
      };
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return ro;
    };
    const a = watch(controlsRef.current, setControlsH);
    const b = watch(diagRef.current, setDiagH);
    return () => { a?.disconnect(); b?.disconnect(); };
  }, []);
  const CONTROLS_BASE_RESERVE = 80; // = paddingBottom панели звонка (без safe-area)
  const stageBottomReserve = Math.max(0, controlsH - CONTROLS_BASE_RESERVE + space.md);
  // Кнопка стоит на top: space.sm; полоса и так начинается на space.xl
  // (padding сетки) — добираем только недостающее плюс зазор space.sm.
  const stripTopReserve = diagH > 0 ? Math.max(0, space.sm + diagH + space.sm - space.xl) : 0;

  // Полоса миниатюр при демонстрации: снизу, если медиазона уже, чем 4:3
  // (телефон, узкое окно), иначе справа.
  const stripAtBottom = gridBox.w > 0 && gridBox.h > 0 ? gridBox.w / gridBox.h < 4 / 3 : false;
  const gridItemCount = tiles.length + audioOnlyParticipants.length;
  /* Ширина плитки 16:9 в px (Антон 19.10 07:52: плитка строго 16:9).
     Было: aspect-ratio + width:100% + max-height:100% — когда ограничителем
     оказывалась высота ряда, браузер оставлял ширину колонки и сжимал
     высоту, плитка выходила ~2.07:1 (замер 1280x720, 4 и 7 участников).
     Стало: ширину считаем тем же подбором колонок и отдаём плиткам через
     CSS-переменную --alg-call-tile-w; высота следует из aspect-ratio. */
  let gridTileWidth = 0;
  let gridOverflow = false;
  const gridColumns = (() => {
    if (oneToOne || gridItemCount === 0) return 1;
    const gap = space.lg;
    const { w, h } = gridBox;
    // До первого замера берём почти-квадратную сетку: разумное приближение
    // на один кадр, после чего ResizeObserver даст точные размеры.
    if (w <= 0 || h <= 0) return Math.ceil(Math.sqrt(gridItemCount));
    let best = 1;
    let bestTileWidth = 0;
    for (let cols = 1; cols <= gridItemCount; cols++) {
      const rows = Math.ceil(gridItemCount / cols);
      const tileWidth = (w - gap * (cols - 1)) / cols;
      const tileHeight = (h - gap * (rows - 1)) / rows;
      if (tileWidth <= 0 || tileHeight <= 0) continue;
      // Плитка 16/9: реальная ширина ограничена и колонкой, и высотой ряда.
      const effective = Math.min(tileWidth, tileHeight * (16 / 9));
      if (effective > bestTileWidth) {
        bestTileWidth = effective;
        best = cols;
      }
    }
    // Читаемый минимум: если лучший вариант всё равно мельче 120px, берём
    // столько колонок, сколько влезает по ширине при этом минимуме, и отдаём
    // остаток в вертикальную прокрутку (overflowY у контейнера уже auto).
    if (bestTileWidth < 120) {
      const fitByWidth = Math.max(1, Math.floor((w + gap) / (120 + gap)));
      const cols = Math.min(gridItemCount, fitByWidth);
      gridTileWidth = Math.floor((w - gap * (cols - 1)) / cols);
      gridOverflow = true;
      return cols;
    }
    gridTileWidth = Math.floor(bestTileWidth);
    return best;
  })();

  /* ПАНЕЛЬ КНОПОК — ТОЛЬКО ПРИ НАВЕДЕНИИ (Антон 19.10 08:25: «Панель
     должна появляться только при mouse over»). Панель висела поверх
     низа видео постоянно и закрывала кадр.
     • Мышь: движение над областью звонка показывает панель; уход мыши
       из области или CONTROLS_IDLE_MS без движения — плавно прячет.
     • Не прячем, пока указатель над самой панелью, пока открыто меню
       устройств (стрелка ^ с aria-expanded=true) и пока внутри панели
       клавиатурный фокус (:focus-visible) — Tab тоже показывает панель.
     • Сенсор без hover (matchMedia('(hover: none)') или pointerType
       touch): НЕ прячем навсегда — тап по области звонка показывает
       панель на CONTROLS_TOUCH_MS. Скрытая панель не ловит тапы
       (pointer-events: none), поэтому тап вслепую не сбросит звонок.
     • Скрываем opacity, а не visibility: иначе кнопки выпали бы из
       порядка Tab и показать панель с клавиатуры было бы нельзя.
     • На старте панель видна CONTROLS_IDLE_MS (чтобы человек видел,
       где кнопки), потом прячется по общему правилу. */
  const CONTROLS_IDLE_MS = 2500;
  const CONTROLS_TOUCH_MS = 4000;
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsHideTimer = useRef<number | null>(null);
  const pointerOverControls = useRef(false);
  const keyboardFocusInControls = useRef(false);
  const hoverNone = useCallback(() => {
    try {
      return typeof window.matchMedia === "function" && window.matchMedia("(hover: none)").matches;
    } catch {
      return false;
    }
  }, []);
  const controlsPinned = useCallback(() => {
    if (pointerOverControls.current || keyboardFocusInControls.current) return true;
    const el = controlsRef.current;
    if (!el) return false;
    // :hover — страховка: если панель появилась под неподвижным указателем,
    // pointerenter может не прийти до следующего движения.
    try { if (!hoverNone() && el.matches(":hover")) return true; } catch { /* old engines */ }
    return !!el.querySelector('[aria-expanded="true"]');
  }, []);
  const scheduleControlsHide = useCallback((ms: number) => {
    if (controlsHideTimer.current !== null) window.clearTimeout(controlsHideTimer.current);
    controlsHideTimer.current = window.setTimeout(function tick() {
      // Закреплена (наведение / меню / фокус) — перепроверим позже, а не
      // бросаем: меню может закрыться по Esc, когда мышь уже ушла.
      if (controlsPinned()) {
        controlsHideTimer.current = window.setTimeout(tick, ms);
        return;
      }
      controlsHideTimer.current = null;
      setControlsVisible(false);
    }, ms);
  }, [controlsPinned]);
  const revealControls = useCallback((ms: number) => {
    setControlsVisible(true);
    scheduleControlsHide(ms);
  }, [scheduleControlsHide]);
  useEffect(() => {
    scheduleControlsHide(CONTROLS_IDLE_MS);
    return () => {
      if (controlsHideTimer.current !== null) window.clearTimeout(controlsHideTimer.current);
    };
  }, [scheduleControlsHide]);
  // Тип указателя события важнее медиа-запроса: гибриды (iPad с трекпадом,
  // ноутбук с сенсором) честно присылают "mouse" для мыши и "touch" для
  // пальца. matchMedia('(hover: none)') — запасной вариант, когда тип пуст.
  const isTouchPointer = (e: ReactPointerEvent) =>
    e.pointerType === "touch" ? true : e.pointerType === "mouse" || e.pointerType === "pen" ? false : hoverNone();
  const onCallPointerMove = (e: ReactPointerEvent) => {
    if (isTouchPointer(e)) return; // сенсор: только по тапу (onPointerDown)
    revealControls(CONTROLS_IDLE_MS);
  };
  const onCallPointerDown = (e: ReactPointerEvent) => {
    revealControls(isTouchPointer(e) ? CONTROLS_TOUCH_MS : CONTROLS_IDLE_MS);
  };
  const onCallPointerLeave = (e: ReactPointerEvent) => {
    if (isTouchPointer(e)) return;
    pointerOverControls.current = false;
    if (controlsPinned()) {
      scheduleControlsHide(CONTROLS_IDLE_MS);
      return;
    }
    if (controlsHideTimer.current !== null) window.clearTimeout(controlsHideTimer.current);
    controlsHideTimer.current = null;
    setControlsVisible(false);
  };

  return (
    <div
      data-testid="call-video-panel"
      /* alg-call-screen: на ≤640px мобильный CSS делает экран звонка
         fixed на весь вьюпорт поверх списка/ленты (спека 2026-09-20,
         этап 4, WhatsApp-стиль). На десктопе класс ни на что не влияет —
         правила живут только внутри @media (max-width:640px). */
      className="alg-call-screen"
      data-controls-visible={controlsVisible ? "true" : "false"}
      onPointerMove={onCallPointerMove}
      onPointerDown={onCallPointerDown}
      onPointerLeave={onCallPointerLeave}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        /* Тёмный фон звонка: видео само тёмное, и светлая кость вокруг
           чёрных плиток резала бы глаз. Тёмно-серый — в палитре (Антон:
           «для контролов звонка»), берём глубокую ступень ink.text —
           она же основа будущей тёмной темы. */
        background: color.text,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        // Reserve the bottom control dock inside the bounded call panel.
        // Without an explicit bounded reserve WebKit could size the media
        // item to its video content and push the complete control row below
        // the visible tablet viewport.
        paddingBottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <div ref={diagRef} data-testid="call-diagnostics-anchor" style={{ position: "absolute", top: space.sm, right: space.sm, zIndex: 35 }}>
        <CallDiagnosticsButton onCopy={onCopyDiagnostics} dark />
      </div>
      {/* 1:1: удалённое видео/экран всегда занимает доступную медиазону,
          локальное превью абсолютно плавает поверх и не участвует в
          расчёте размеров. 3+ участников: адаптивная равная сетка. */}
      <div
        ref={gridRef}
        data-testid="call-grid"
        className="alg-call-grid"
        data-grid-columns={oneToOne || stageTile ? undefined : gridColumns}
        data-layout={oneToOne ? "one-to-one" : stageTile ? "screen-stage" : "grid"}
        style={stageTile ? {
          flex: "1 1 auto",
          minHeight: 0,
          position: "relative",
          display: "flex",
          // Широкое окно — полоса справа; узкое/портретное — снизу.
          flexDirection: stripAtBottom ? "column" : "row",
          gap: space.md,
          padding: space.xl,
          // Сцена вместе с подписью целиком над панелью кнопок (см. выше).
          marginBottom: stageBottomReserve,
          overflow: "hidden",
        } : {
          flex: "1 1 auto",
          minHeight: 0,
          position: "relative",
          display: oneToOne ? "flex" : "grid",
          // Расчётное число колонок (см. gridColumns выше) вместо auto-fit с
          // жёстким минимумом 240px: только так плитки растут, пока есть место.
          gridTemplateColumns: oneToOne ? undefined : `repeat(${gridColumns}, minmax(0, 1fr))`,
          // Ряды тоже делят высоту поровну: без этого ряд сжимается до высоты
          // содержимого, и пустое поле внизу возвращается.
          // При переполнении (плитка < 120px) ряды по содержимому, остаток
          // уходит в прокрутку; иначе 16:9-плитка не влезла бы в ряд.
          gridAutoRows: oneToOne ? undefined : gridOverflow ? "auto" : "minmax(0, 1fr)",
          ...(oneToOne || gridTileWidth <= 0
            ? {}
            : ({ "--alg-call-tile-w": `${gridTileWidth}px` } as CSSProperties)),
          gap: oneToOne ? undefined : space.lg,
          padding: space.xl,
          alignItems: oneToOne ? "stretch" : "stretch",
          alignContent: "stretch",
          justifyContent: "center",
          overflowY: "auto",
        }}
      >
        {oneToOne ? (
          <>
            {primaryRemote ? (
              <CallTileView tile={primaryRemote} main />
            ) : remoteParticipant ? (
              <CallParticipantView
                client={client}
                participant={remoteParticipant}
                cameraOff={isCameraOff(remoteParticipant)}
                main
              />
            ) : null}
            {localTiles.length > 0
              ? localTiles.map((tile, index) => (
                  <div
                    key={tile.key}
                    style={{
                      position: "absolute",
                      right: space.xl,
                      bottom: space.xl + index * 112,
                      width: "clamp(112px, 22vw, 200px)",
                      zIndex: 2,
                    }}
                  >
                    <CallTileView tile={tile} micro />
                  </div>
                ))
              : localParticipant && (
                  <div
                    style={{
                      position: "absolute",
                      right: space.xl,
                      bottom: space.xl,
                      width: "clamp(112px, 22vw, 200px)",
                      zIndex: 2,
                    }}
                  >
                    <CallParticipantView
                      client={client}
                      participant={localParticipant}
                      cameraOff={isCameraOff(localParticipant)}
                      micro
                    />
                  </div>
                )}
          </>
        ) : stageTile ? (
          <>
            <div
              data-testid="call-stage"
              style={{ flex: "1 1 auto", minWidth: 0, minHeight: 0, display: "flex" }}
            >
              <CallTileView key={stageTile.key} tile={stageTile} main />
            </div>
            <div
              data-testid="call-strip"
              style={{
                flex: stripAtBottom ? "0 0 clamp(72px, 18%, 120px)" : "0 0 clamp(120px, 16%, 220px)",
                // Полоса справа начинается под кнопкой диагностики: ни одна
                // миниатюра не уходит под неё. Полоса снизу кнопки не касается.
                marginTop: stripAtBottom ? undefined : stripTopReserve,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: stripAtBottom ? "row" : "column",
                gap: space.sm,
                overflowX: stripAtBottom ? "auto" : "hidden",
                overflowY: stripAtBottom ? "hidden" : "auto",
              }}
            >
              {stripTiles.map((tile) => {
                const pinnable = !tile.local && tile.kind === "screen";
                return (
                  <div
                    key={tile.key}
                    data-testid="call-strip-item"
                    role={pinnable ? "button" : undefined}
                    tabIndex={pinnable ? 0 : undefined}
                    title={pinnable ? tile.label : undefined}
                    onClick={pinnable ? () => pinScreen(tile.key) : undefined}
                    onKeyDown={pinnable ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        pinScreen(tile.key);
                      }
                    } : undefined}
                    style={stripItemStyle(stripAtBottom, pinnable)}
                  >
                    <CallTileView tile={tile} micro />
                  </div>
                );
              })}
              {audioOnlyParticipants.map((participant) => (
                <div key={`audio:${participant.identity}`} data-testid="call-strip-item" style={stripItemStyle(stripAtBottom, false)}>
                  <CallParticipantView
                    client={client}
                    participant={participant}
                    cameraOff={isCameraOff(participant)}
                    micro
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {tiles.map((tile) => <CallTileView key={tile.key} tile={tile} />)}
            {audioOnlyParticipants.map((participant) => (
              <CallParticipantView
                key={`audio:${participant.identity}`}
                client={client}
                participant={participant}
                cameraOff={isCameraOff(participant)}
              />
            ))}
          </>
        )}
        {/* Состояние «звоню, никто не ответил»: своего видео может не
            быть (аудиозвонок), и сетка тогда пуста — показываем плашку
            ожидания, чтобы экран не был чёрным провалом. Отсчёт
            (soloSecondsLeft) не переписываем, только показываем. */}
        {(callParticipants.length === 0 || (oneToOne && !primaryRemote && !remoteParticipant)) && (
          <div
            style={{
              gridColumn: oneToOne ? undefined : "1 / -1",
              margin: "auto",
              textAlign: "center",
              color: "rgba(255,255,255,0.85)",
              fontSize: 15,
              lineHeight: 1.5,
            }}
            data-testid="call-waiting"
          >
            {/* «ЖДЁМ ОТВЕТА» — ТОЛЬКО ПОКА РЕАЛЬНО ЖДЁМ (Антон 12.09
                10:24: «идёт звонок, ждём ответа — ответ уже есть»).

                БЫЛО: надпись показывалась по ОТСУТСТВИЮ ВИДЕОПЛИТОК. В
                аудиозвонке плиток нет никогда, поэтому «ждём ответа»
                висело и во время живого разговора. Считать ожидание по
                видео неверно в принципе: аудиозвонок — полноценный
                разговор без единого кадра.

                СТАЛО: смотрим на ЛЮДЕЙ. participants <= 1 = «я один»,
                вот это и есть ожидание; как только человек ответил,
                текст меняется на состояние разговора. */}
            {participants <= 1 ? (
              <>
                {t("call.ringingWaitAnswer")}
                {waiting && (
                  <div style={{ fontSize: 13, opacity: 0.65, marginTop: 4 }}>
                    {tfmt("call.noAnswerCountdown", {
                      seconds: String(soloSecondsLeft).padStart(2, "0"),
                    })}
                  </div>
                )}
              </>
            ) : (
              <>{t("room.callInProgress")}<div style={{ fontSize: 13, opacity: 0.65, marginTop: 4 }}>
                  {tfmt("timeline.callParticipantsCount", { count: participants })}{t("room.noVideoYet")}</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ТИТРЫ поверх видео: сообщения комнаты, пришедшие во время
          звонка (Антон 2026-09-12 08:34). Контейнер абсолютный внутри
          экрана звонка, нижняя треть, над строкой ввода и контролами. */}
      <CallCaptions items={captionItems} />

      {/* Подпись ожидания над контролами, когда видео уже есть, но ответа
          ещё нет (видеозвонок: своё превью видно, собеседник не взял). */}
      {waiting && (count > 0 || callParticipants.length > 0) && (
        <div
          style={{
            flex: "0 0 auto",
            textAlign: "center",
            color: "rgba(255,255,255,0.75)",
            fontSize: 13,
            paddingBottom: space.sm,
          }}
        >
          {tfmt("call.waitingCountdown", {
            seconds: String(soloSecondsLeft).padStart(2, "0"),
          })}
        </div>
      )}

      {/* Не вышло включить камеру — говорим текстом, а не молча.
          Звонок при этом НЕ рвётся: аудио идёт, не вышло только видео.
          Частый случай — отказ в доступе или камера занята другим
          приложением; без текста человек жмёт кнопку вхолостую. */}
      {cameraError && (
        <div
          data-testid="call-camera-error"
          style={{
            flex: "0 0 auto",
            textAlign: "center",
            color: color.textOnAccent,
            background: palette.danger,
            fontSize: 13,
            padding: `${space.xs}px ${space.lg}px`,
            margin: `0 ${space.xl}px ${space.sm}px`,
            borderRadius: radius.sm,
          }}
        >
          {tfmt("call.cameraFailed", { error: cameraError })}
        </div>
      )}
      {/* То же правило для демонстрации экрана (screen share 12:28):
          не вышло — говорим текстом, звонок не рвётся (п.4 постановки). */}
      {screenError && (
        <div
          data-testid="call-screen-error"
          style={{
            flex: "0 0 auto",
            textAlign: "center",
            color: color.textOnAccent,
            background: palette.danger,
            fontSize: 13,
            padding: `${space.xs}px ${space.lg}px`,
            margin: `0 ${space.xl}px ${space.sm}px`,
            borderRadius: radius.sm,
          }}
        >
          {tfmt("call.screenShareFailed", { error: screenError })}
        </div>
      )}
      {/* Уведомление про УСТРОЙСТВА (задача 2026-09-13): выбранный
          микрофон/камера/динамик отключён — вернулись на умолчание.
          Постановка п.3: «сказать об этом человеку текстом, не молча».
          НЕ красный: это не ошибка действия человека, а инфо о смене
          железа — фон приглушённый, как у «ждём ответа». */}
      {deviceNotice && (
        <div
          data-testid="call-device-notice"
          style={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: space.md,
            color: "rgba(255,255,255,0.9)",
            background: "rgba(255,255,255,0.12)",
            fontSize: 13,
            padding: `${space.xs}px ${space.lg}px`,
            margin: `0 ${space.xl}px ${space.sm}px`,
            borderRadius: radius.sm,
          }}
        >
          <span>{deviceNotice}</span>
          <button
            type="button"
            onClick={onDismissDeviceNotice}
            style={{
              font: "inherit",
              border: "none",
              background: "none",
              color: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >{t("app.hideActionError")}</button>
        </div>
      )}

      {/* ОТДЕЛЬНОЙ СТРОКИ ВВОДА ЗДЕСЬ НЕТ — и не будет.
          Постановка Антона 12.09 09:20 (голосом): вместо второго поля
          ввода внутри звонка экран СВОРАЧИВАЕТСЯ в мини-фрейм, и человек
          пишет в ОБЫЧНЫЙ композер комнаты, видя, что печатает. Вход в
          переписку — кнопка «написать» в ряду контролов ниже. */}

      {/* Контролы звонка — 1 В 1 ПО ЭТАЛОНУ iOS (задача Антона 05.10
          08:52, замер по alg-messenger-ios/algsuccess/Calls/CallView.swift,
          CallControlsView :1472 и ControlButton :2059):

          ГЕОМЕТРИЯ:
            • ДВА РЯДА, зазор между рядами 10px (VStack spacing 10).
            • Верхний ряд: Динамик → Экран → Пригласить → Перевернуть
              камеру (последняя — только если камер больше одной, аналог
              canToggleCamera = isTelephony || cameras.count > 1).
            • Нижний ряд: ОТБОЙ → Микрофон → Камера. Отбой стоит В РЯДУ
              наравне с остальными, НЕ отдельной большой кнопкой.
            • Зазор между колонками 8px (grid/column spacing 8 в обоих
              рядах; grid spacing 12 у сетки iOS — это её вертикальный
              ритм внутри LazyVGrid, у нас ряды разнесены теми же 10).
            • Подложка: полупрозрачный blur-прямоугольник (аналог
              ultraThinMaterial), скругление 20px; padding 16 внутри по
              горизонтали / 12 снаружи / 12 по вертикали.

          ПОДПИСИ ПОД КНОПКАМИ РИСУЕМ — КАК В iOS (Антон 05.10: «под
          кнопками управления звонком нет подписей, а в iOS они есть»).
          Запрет от 05.10 08:51 («убери текст под кнопками») ОТМЕНЁН этой
          же задачей: эталон перепроверен, в iOS Text(label) под иконкой
          ЕСТЬ (CallView.swift, ControlButton — caption, по центру, белый
          80%). Подпись СТАТИЧНА — как в iOS: label не зависит от
          состояния тумблера («Микрофон» и при мьютe, «Камера» и при
          выключенном видео); состояние показывает только иконка.
          Формулировки — из iOS Localizable.xcstrings (call.speaker,
          call.microphone, call.camera, call.invite, call.end,
          call.flipCamera), ключи веба call.label*. Полный title/aria-label
          с глаголом («Включить микрофон»…) сохранён — как в iOS
          accessibilityLabel.

          ОТЛИЧИЯ ОТ СТАРОГО ВЕБА, которые НЕ удалены молча (решение за
          Антоном, перечислены в отчёте):
            • кнопка «Написать» (свернуть звонок в окошко) — в iOS её нет,
              оставлена в конце верхнего ряда;
            • стрелки-меню выбора устройств у микрофона/камеры/динамика —
              в iOS выбор устройств нативный, в вебе он живёт в этих меню;
            • красная подсветка выключенного микрофона (постановка 12.09
              10:29) СОХРАНЕНА по прямому указанию задачи — единственное
              сознательное отступление от эталона iOS. */}
      <div
        ref={controlsRef}
        data-testid="call-controls"
        data-visible={controlsVisible ? "true" : "false"}
        onPointerEnter={(e) => {
          if (e.pointerType === "touch") return;
          pointerOverControls.current = true;
          setControlsVisible(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "touch") return;
          pointerOverControls.current = false;
          scheduleControlsHide(CONTROLS_IDLE_MS);
        }}
        onFocus={(e) => {
          // Только клавиатурный фокус закрепляет панель: после клика
          // мышью кнопка тоже в фокусе, и тогда панель не пряталась бы никогда.
          let kb = false;
          try { kb = (e.target as HTMLElement).matches(":focus-visible"); } catch { kb = false; }
          if (!kb) return;
          keyboardFocusInControls.current = true;
          revealControls(CONTROLS_IDLE_MS);
        }}
        onBlur={(e) => {
          if (controlsRef.current?.contains(e.relatedTarget as Node | null)) return;
          keyboardFocusInControls.current = false;
          scheduleControlsHide(CONTROLS_IDLE_MS);
        }}
        style={{
          opacity: controlsVisible ? 1 : 0,
          transition: controlsVisible ? "opacity 180ms ease-out" : "opacity 400ms ease-in",
          pointerEvents: controlsVisible ? "auto" : "none",
          flex: "0 0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "env(safe-area-inset-bottom, 0px)",
          zIndex: 40,
          /* Снаружи подложки — 8px по горизонтали (было 12): при пяти
             колонках по 56px и зазорах 8 на экране 360px ряд иначе не
             влезал бы (5·56+4·8=312). Градиент затемнения сохранён:
             без него белые круги терялись бы на светлом видео. */
          padding: `0 8px 12px`,
          background: "linear-gradient(transparent, rgba(0,0,0,0.72))",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8, // было 10 (VStack spacing 10); -2px компенсация за подписи
            boxSizing: "border-box",
            maxWidth: "100%",
            /* Аналог ultraThinMaterial: полупрозрачная подложка + blur.
               Скруглённый прямоугольник. Горизонтальный padding 12 (было
               16) — чтобы ряд из пяти колонок по 56px влезал в 360px
               (5·56+4·8=312 ≤ 360−2·8−2·12=320); вертикальный 10 (было
               12) — частичная компенсация высоты подписей. Круги 56
               не тронуты. */
            background: "rgba(30,30,32,0.55)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            borderRadius: 20,
            padding: "10px 12px",
          }}
        >
          {/* ВЕРХНИЙ РЯД: Динамик → Экран → Пригласить → Перевернуть
              камеру (если камер > 1). «Написать» — лишняя относительно
              iOS, оставлена в конце (решение за Антоном).
              КОЛОНКИ РАВНОЙ ШИРИНЫ, БЕЗ ПЕРЕНОСА — аналог LazyVGrid с
              фиксированным числом столбцов: каждая ячейка flex:1,
              содержимое по центру. */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              /* ВЫРАВНИВАНИЕ СВЕРХУ, а не по центру — как в iOS
                 (.frame(maxHeight: .infinity, alignment: .top)): у колонки
                 с двухстрочной подписью круг иначе уехал бы ниже кругов
                 соседей. Подписи разной высоты висят ВНИЗУ, круги всех
                 колонок стоят на одной линии. */
              alignItems: "flex-start",
              gap: 8, // spacing между колонками 8 (эталон iOS)
              flexWrap: "nowrap",
              alignSelf: "stretch",
            }}
          >
            {/* ДИНАМИК. В iOS это toggle аудиомаршрута (speakerPhone);
                в веб-API звонка прямого toggle маршрута нет — rtc.ts не
                трогаем по существу. Кнопка поэтому отражает, выбран ли
                конкретный динамик (activeDevices.speaker !== ""), а
                стрелка рядом открывает существующее меню выбора
                устройства вывода. Зафиксировано в отчёте как отличие. */}
            {devices.speakers.length > 0 && (
              <CallControlColumn label={t("call.labelSpeaker")} hasMenu>
                <CallControlButton
                  testId="call-speaker"
                  title={t("call.deviceSpeaker")}
                  on={activeDevices.speaker !== ""}
                  onClick={() => {
                    /* Быстрый toggle без rtc-правок: если выбран
                       не-дефолтный динамик — возвращаем системный;
                       иначе выбираем первый из списка (тот же
                       selectDevice, что и у стрелки-меню). */
                    const next = activeDevices.speaker === "" ? (devices.speakers[0]?.deviceId ?? "") : "";
                    onSelectDevice("speaker", next, next === "" ? "" : (devices.speakers[0]?.label ?? ""));
                  }}
                >
                  <IconSpeaker size={24} />
                </CallControlButton>
                <DeviceMenuButton
                  testId="call-speaker-device-menu"
                  title={t("call.deviceSpeaker")}
                  groups={[
                    { heading: t("call.deviceSpeaker"), kind: "speaker" as const, items: devices.speakers, activeId: activeDevices.speaker },
                  ]}
                  onSelect={onSelectDevice}
                />
              </CallControlColumn>
            )}
            {/* ДЕМОНСТРАЦИЯ ЭКРАНА. Порядок вторым — как в iOS. Белая в
                обоих состояниях; активная демонстрация = белая иконка
                полной яркости, неактивная = 70% (правило ControlButton
                iOS). */}
            <CallControlColumn label={t("call.labelScreenShare")}>
              <CallControlButton
                testId="call-toggle-screen"
                title={
                  !screenSupported
                    ? t("ui.demonstraciya_ekrana_nedostupna_v_etom_br")
                    : screenBusy
                      ? t("ui.vybiraem_okno_ili_ekran")
                      : screenOn
                        ? t("ui.ostanovit_demonstraciyu_ekrana")
                        : t("ui.demonstraciya_ekrana")
                }
                on={screenOn}
                /* Приглушена = «нажать нельзя»: браузер без getDisplayMedia
                   или висит системный выбор окна. */
                disabled={!screenSupported || screenBusy}
                onClick={onToggleScreenShare}
              >
                {screenOn ? <IconScreenShare size={24} /> : <IconScreenShareOff size={24} />}
              </CallControlButton>
            </CallControlColumn>
            {/* ПРИГЛАСИТЬ — третьим, как в iOS. Рисуем только когда
                обработчик передан (в пропе-заглушке приглашать некуда). */}
            {onAddParticipant && (
              <CallControlColumn label={t("call.labelInvite")}>
                <CallControlButton
                  testId="call-add-participant"
                  title={t("room.inviteMenu")}
                  on={true}
                  onClick={onAddParticipant}
                >
                  <IconUserPlus size={24} />
                </CallControlButton>
              </CallControlColumn>
            )}
            {/* ПЕРЕВЕРНУТЬ КАМЕРУ — только если камер больше одной
                (аналог iOS canToggleCamera). Реализовано БЕЗ правок
                rtc.ts: циклический перебор devices.cameras через
                существующий selectDevice — тот же механизм, что у меню
                выбора камеры. */}
            {devices.cameras.length > 1 && canFlipCameraPlatform() && (
              <CallControlColumn label={t("call.labelFlipCamera")}>
                <CallControlButton
                  testId="call-flip-camera"
                  title={t("ui.perevernut_kameru")}
                  on={true}
                  onClick={() => {
                    const ids = devices.cameras.map((c) => c.deviceId);
                    const idx = ids.indexOf(activeDevices.camera);
                    const next = devices.cameras[(idx + 1) % devices.cameras.length];
                    if (next) onSelectDevice("camera", next.deviceId, next.label);
                  }}
                >
                  <IconCameraFlip size={24} />
                </CallControlButton>
              </CallControlColumn>
            )}
            {/* «НАПИСАТЬ» (Антон 12.09 09:20): сворачивает полный экран в
                CallMiniFrame, лента и обычный композер открываются. В iOS
                этой кнопки НЕТ — оставлена по прямому указанию задачи
                («не удаляй молча, перечисли»), стоит последней в верхнем
                ряду. */}
            {onCompose && (
              <CallControlColumn label={t("call.labelCompose")}>
                <CallControlButton
                  testId="call-compose"
                  title={t("ui.svernut_zvonok_v_okoshko_chtoby_pisat_v")}
                  on={true}
                  onClick={onCompose}
                >
                  <IconMinimize size={24} />
                </CallControlButton>
              </CallControlColumn>
            )}
          </div>
          {/* НИЖНИЙ РЯД: ОТБОЙ → Микрофон → Камера. Отбой В РЯДУ наравне
              с остальными (эталон iOS). Колонки равной ширины, без
              переноса — как в верхнем ряду. */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start", // как в верхнем ряду: круги на одной линии
              gap: 8,
              flexWrap: "nowrap",
              alignSelf: "stretch",
            }}
          >
            {/* «Завершить» / «Отменить звонок»: режим выбирается тем же
                soloSecondsLeft, что и раньше, — логика не тронута. Подпись
                СТАТИЧНАЯ «Завершить» (как в iOS — call.end не зависит от
                состояния); «Отменить звонок» остаётся в title/aria-label. */}
            <CallControlColumn label={t("call.labelEnd")}>
              <CallControlButton
                testId="call-leave"
                title={waiting ? t("ui.otmenit_zvonok") : t("room.callLeave")}
                on={true}
                danger
                onClick={onLeave}
              >
                <IconPhoneOff size={24} />
              </CallControlButton>
            </CallControlColumn>
            {/* МИКРОФОН. ВЫКЛЮЧЕННЫЙ — КРАСНЫЙ: отдельная постановка
                Антона 12.09 10:29 («очень полезно, когда мьют микрофон,
                у тебя ещё выделен красным — тогда ты точно не забываешь
                его включить»), сохранена по прямому указанию задачи
                05.10 — это единственное сознательное отступление от
                эталона iOS, где красный только у отбоя. Камера при этом
                остаётся белой перечёркнутой (там же, 10:29). Стрелка-меню
                — выбор микрофона и динамика. */}
            <CallControlColumn label={t("call.labelMicrophone")} hasMenu>
              <CallControlButton
                testId="call-toggle-mic"
                title={micOn ? t("ui.vyklyuchit_mikrofon") : t("ui.vklyuchit_mikrofon")}
                on={micOn}
                offTone={micOn ? undefined : "red"}
                onClick={onToggleMic}
              >
                {micOn ? <IconMic size={24} /> : <IconMicOff size={24} />}
              </CallControlButton>
              <DeviceMenuButton
                testId="call-mic-device-menu"
                title={t("ui.vybrat_mikrofon_i_dinamik")}
                groups={[
                  { heading: t("call.deviceMicrophone"), kind: "mic" as const, items: devices.mics, activeId: activeDevices.mic },
                  ...(devices.speakers.length > 0
                    ? [{ heading: t("call.deviceSpeaker"), kind: "speaker" as const, items: devices.speakers, activeId: activeDevices.speaker }]
                    : []),
                ]}
                onSelect={onSelectDevice}
              />
            </CallControlColumn>
            {/* Камера. В АУДИОЗВОНКЕ ТОЖЕ РАБОТАЕТ (Антон 12.09 09:21):
                первое включение запрашивает устройство и публикует трек.
                Пока висит системный запрос доступа — кнопка заблокирована.
                Стрелка-меню — выбор камеры. */}
            <CallControlColumn label={t("call.labelCamera")} hasMenu>
              <CallControlButton
                testId="call-toggle-camera"
                title={
                  cameraBusy
                    ? t("ui.zaprashivaem_dostup_k_kamere")
                    : cameraOn
                      ? t("ui.vyklyuchit_kameru")
                      : cameraInitialized
                        ? t("ui.vklyuchit_kameru")
                        : t("ui.vklyuchit_kameru_zaprosim_dostup")
                }
                on={cameraOn}
                disabled={cameraBusy}
                onClick={onToggleCamera}
              >
                {cameraOn ? <IconVideo size={24} /> : <IconVideoOff size={24} />}
              </CallControlButton>
              <DeviceMenuButton
                testId="call-camera-device-menu"
                title={t("ui.vybrat_kameru")}
                groups={[
                  { heading: t("call.deviceCamera"), kind: "camera" as const, items: devices.cameras, activeId: activeDevices.camera },
                ]}
                onSelect={onSelectDevice}
              />
            </CallControlColumn>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Кнопка экспорта без технических инструментов + короткая обратная связь. */
function CallDiagnosticsButton({
  onCopy,
  dark = false,
}: {
  onCopy: () => Promise<void>;
  dark?: boolean;
}) {
  const t = useT();
  const [feedback, setFeedback] = useState<"ok" | "error" | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  const copy = () => {
    void onCopy().then(
      () => setFeedback("ok"),
      () => setFeedback("error"),
    ).finally(() => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setFeedback(null), 2500);
    });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: space.sm }}>
      <button
        type="button"
        data-testid="copy-call-diagnostics"
        onClick={copy}
        style={{
          border: dark ? "1px solid rgba(255,255,255,0.35)" : `1px solid ${color.border}`,
          borderRadius: radius.sm,
          background: dark ? "rgba(255,255,255,0.12)" : color.feedBg,
          color: dark ? "#fff" : color.text,
          padding: "7px 11px",
          font: "inherit",
          fontSize: 12,
          cursor: "pointer",
        }}
      >
        {t("call.copyDiagnostics")}
      </button>
      {feedback && (
        <span role="status" style={{ fontSize: 12, color: feedback === "ok" ? (dark ? "#fff" : color.ok) : palette.danger }}>
          {feedback === "ok" ? t("call.diagnosticsCopied") : t("call.diagnosticsCopyFailed")}
        </span>
      )}
    </div>
  );
}

/** Ячейка полосы миниатюр рядом со сценой демонстрации (2026-10-15). */
function stripItemStyle(atBottom: boolean, pinnable: boolean): CSSProperties {
  return {
    flex: "0 0 auto",
    ...(atBottom ? { height: "100%", aspectRatio: "16/9" } : { width: "100%", aspectRatio: "16/9" }),
    display: "flex",
    cursor: pinnable ? "pointer" : undefined,
    borderRadius: radius.md,
  };
}

/** Одна плитка участника. main заполняет медиазону 1:1, micro —
 * локальный плавающий preview; обычный режим — ячейка групповой сетки. */
function CallTileView({
  tile,
  main = false,
  micro = false,
}: {
  tile: CallTile;
  main?: boolean;
  micro?: boolean;
}) {
  return (
    <div
      style={{
        position: "relative",
        borderRadius: radius.md,
        overflow: "hidden",
        background: "#000",
        ...(main
          ? { flex: "1 1 auto", minWidth: 0, minHeight: 0 }
          : // Растём под ячейку сетки, сохраняя 16/9 и не вылезая за неё ни по
            // ширине, ни по высоте (п.7, 2026-09-18). margin:auto центрирует плитку
            // в ячейке, когда ограничителем оказалась высота, а не ширина.
            // Ширина — расчётная из сетки (--alg-call-tile-w), высота —
            // из aspect-ratio; max-height не ставим: он ломал 16:9.
            // Вне сетки (полоса, PiP) переменной нет — 100% контейнера.
            {
              aspectRatio: "16/9",
              width: "min(100%, var(--alg-call-tile-w, 100%))",
              maxWidth: "100%",
              minWidth: 0,
              minHeight: 0,
              margin: "auto",
            }),
        /* Speaker state comes from LiveKit; an outer shadow preserves the
           rounded crop and never changes grid/PiP geometry. */
        boxShadow: tile.speaking
          ? `0 0 0 3px ${color.danger}, 0 4px 18px rgba(0,0,0,0.55)`
          : micro
            ? "0 4px 18px rgba(0,0,0,0.55)"
            : "none",
      }}
      role="group"
      aria-label={tile.label}
      aria-current={tile.speaking ? "true" : undefined}
      data-speaking={tile.speaking ? "true" : undefined}
    >
      <VideoElement
        stream={tile.stream}
        muted={tile.local}
        /* Камера в 16:9-плитке (сетка, полоса, своё превью) — "tile16x9":
           кадр не обрезается, вертикальный — центральный квадрат с чёрными
           полосами (Антон 19.10 07:52). Большое видео 1:1 (main) и экран —
           contain: кадр ЛЮБОЙ формы, включая вертикальный, целиком на всю
           область звонка, пустое место — чёрный фон плитки (Антон 19.10
           08:09: «1 на 1 видео идет без кропа на весь доступный
           фрейм/экран, пустые места заполняем черным»). Центральный
           квадрат — только для плиток сетки. */
        fit={tile.kind === "screen" || main ? "contain" : "tile16x9"}
        zoomable={!tile.local && tile.kind === "screen"}
        tileKey={tile.key}
      />
      {/* Имя — по-человечески: снизу по центру, нормального размера, на
          градиентной подложке, а не мелочью в углу. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "18px 8px 8px",
          background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
          color: "#fff",
          fontSize: 14,
          fontWeight: 600,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {tile.label}
      </div>
      {tile.micMuted && <MicMutedBadge micro={micro} />}
    </div>
  );
}

/** Значок «микрофон выключен» в углу плитки участника (Антон 12.10). */
function MicMutedBadge({ micro = false }: { micro?: boolean }) {
  const t = useT();
  const label = t("rtc.micMuted");
  return (
    <div
      data-testid="call-mic-muted"
      role="img"
      aria-label={label}
      title={label}
      style={{
        position: "absolute",
        top: micro ? 4 : 8,
        right: micro ? 4 : 8,
        width: micro ? 20 : 28,
        height: micro ? 20 : 28,
        borderRadius: radius.circle,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,0.6)",
        color: "#fff",
        pointerEvents: "none",
      }}
    >
      <IconMicOff size={micro ? 12 : 16} />
    </div>
  );
}

/** Audio-only participant: avatar/initials occupy a normal call tile and
 * use the same measured LiveKit speaker border as video tiles.
 * cameraOff — та же плитка служит заглушкой выключенной камеры (Антон
 * 12.10): добавляется значок перечёркнутой камеры. */
function CallParticipantView({
  client,
  participant,
  main = false,
  micro = false,
  cameraOff = false,
}: {
  client: MatrixClient;
  participant: CallParticipant;
  main?: boolean;
  micro?: boolean;
  cameraOff?: boolean;
}) {
  const { url } = useAuthedMedia(client, participant.avatarMxc);
  const initials = participant.label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

  return (
    <div
      data-testid="call-audio-participant"
      data-speaking={participant.speaking ? "true" : undefined}
      role="group"
      aria-label={participant.label}
      aria-current={participant.speaking ? "true" : undefined}
      style={{
        position: "relative",
        display: "grid",
        placeItems: "center",
        borderRadius: radius.md,
        overflow: "hidden",
        background: colorOf(participant.identity),
        ...(main
          ? { flex: "1 1 auto", minWidth: 0, minHeight: 0 }
          : // Та же геометрия, что у видеоплитки (п.7): аудио-участник стоит в той
            // же сетке и обязан масштабироваться одинаково с видео.
            // Ширина — расчётная из сетки (--alg-call-tile-w), высота —
            // из aspect-ratio; max-height не ставим: он ломал 16:9.
            // Вне сетки (полоса, PiP) переменной нет — 100% контейнера.
            {
              aspectRatio: "16/9",
              width: "min(100%, var(--alg-call-tile-w, 100%))",
              maxWidth: "100%",
              minWidth: 0,
              minHeight: 0,
              margin: "auto",
            }),
        boxShadow: participant.speaking
          ? `0 0 0 3px ${color.danger}, 0 4px 18px rgba(0,0,0,0.55)`
          : micro
            ? "0 4px 18px rgba(0,0,0,0.55)"
            : "none",
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          style={{
            width: micro ? "72%" : "min(42%, 180px)",
            aspectRatio: "1",
            borderRadius: radius.circle,
            objectFit: "cover",
            boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{
            width: micro ? "58%" : "min(34%, 144px)",
            aspectRatio: "1",
            borderRadius: radius.circle,
            display: "grid",
            placeItems: "center",
            background: "rgba(255,255,255,0.2)",
            color: "#fff",
            fontSize: micro ? 22 : "clamp(28px, 5vw, 56px)",
            fontWeight: 700,
          }}
        >
          {initials}
        </div>
      )}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: micro ? "10px 5px 5px" : "18px 8px 8px",
          background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
          color: "#fff",
          fontSize: micro ? 11 : 14,
          fontWeight: 600,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {participant.label}
      </div>
      {participant.micMuted && <MicMutedBadge micro={micro} />}
      {cameraOff && <CameraOffBadge micro={micro} />}
    </div>
  );
}

/** Значок «камера выключена» в левом верхнем углу плитки-заглушки
 * (Антон 12.10); правый угол занят значком микрофона. */
function CameraOffBadge({ micro = false }: { micro?: boolean }) {
  const t = useT();
  const label = t("rtc.cameraOff");
  return (
    <div
      data-testid="call-camera-off"
      role="img"
      aria-label={label}
      title={label}
      style={{
        position: "absolute",
        top: micro ? 4 : 8,
        left: micro ? 4 : 8,
        width: micro ? 20 : 28,
        height: micro ? 20 : 28,
        borderRadius: radius.circle,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,0.6)",
        color: "#fff",
        pointerEvents: "none",
      }}
    >
      <IconVideoOff size={micro ? 12 : 16} />
    </div>
  );
}

/** Колонка контрола звонка: кнопка (или кнопка + стрелка меню устройств)
    и ПОДПИСЬ ПОД НЕЙ — как в эталоне iOS ControlButton (задача Антона
    05.10, замер по CallView.swift: Text(label) под иконкой, шрифт
    caption, по центру, белый 80%).

    • Подпись СТАТИЧНА, как в iOS: она называет кнопку, а не состояние
      (у выключенного микрофона всё равно «Микрофон»). Состояние
      показывает иконка; полный глагол остаётся в title/aria-label.
    • Максимум ДВЕ СТРОКИ (в iOS lineLimit сжатие; на вебе — clamp(2)),
      по центру; длинное слово обрезается многоточием, а не рвёт ряд.
    • Ширина колонки — flex:1 1 0, minWidth:0: ряд не распирает даже
      на 360px при пяти колонках (круг 56 + зазоры 8 влезают за счёт
      уменьшенных полей панели). */
function CallControlColumn({
  label,
  hasMenu,
  children,
}: {
  /** Видимая подпись под кнопкой (уже локализованная, статичная). */
  label: string;
  /** Рядом с кнопкой есть стрелка DeviceMenuButton — нужен row-gap 4
      между кнопкой и стрелкой (старая компоновка) и position:relative
      для выпадающего меню. */
  hasMenu?: boolean;
  children: ReactNode;
}) {
  /* ПОДПИСЬ ТОЛЬКО ПРИ НАВЕДЕНИИ НА ДЕСКТОПЕ (Антон 12.10): на Mac/браузере
     подпись под кнопкой скрыта и всплывает над кнопкой при наведении мыши.
     На тач-платформе (Android Capacitor / pointer:coarse) наведения нет —
     подпись остаётся видимой, как было. Тултип position:absolute — высота
     колонки при наведении не меняется. Нативный title не берём: у кнопки
     он уже занят полным глаголом (aria-label), а в WKWebView нативные
     подсказки показываются с задержкой и не всегда. */
  const [touch] = useState(isTouchCallPlatform);
  const [hover, setHover] = useState(false);
  if (!touch) {
    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          flex: "1 1 0",
          minWidth: 0,
        }}
      >
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: hasMenu ? 4 : 0,
            minWidth: 0,
          }}
        >
          {children}
          {hover && (
            <span
              role="tooltip"
              data-testid="call-control-hover-label"
              style={{
                position: "absolute",
                bottom: "calc(100% + 6px)",
                left: "50%",
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
                fontSize: 12,
                lineHeight: 1.15,
                color: "#fff",
                background: "rgba(0,0,0,0.75)",
                borderRadius: 6,
                padding: "4px 8px",
                userSelect: "none",
                pointerEvents: "none",
                zIndex: 5,
              }}
            >
              {label}
            </span>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        position: hasMenu ? "relative" : undefined,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 4,
        flex: "1 1 0",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: hasMenu ? 4 : 0,
          minWidth: 0,
        }}
      >
        {children}
      </div>
      <span
        style={{
          fontSize: 12, // caption эталона iOS
          lineHeight: 1.15,
          color: "rgba(255,255,255,0.8)", // white 80%, как в iOS
          textAlign: "center",
          maxWidth: "100%",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          textOverflow: "ellipsis",
          overflowWrap: "break-word",
          userSelect: "none",
          pointerEvents: "none", // подпись не должна перехватывать тап
        }}
      >
        {label}
      </span>
    </div>
  );
}

/** Круглая кнопка контрола звонка — геометрия 1 В 1 ПО ЭТАЛОНУ iOS
    (задача Антона 05.10 08:52, замер по alg-messenger-ios/.../CallView.swift,
    ControlButton :2059):
      • круг диаметр 56, ПОТОЛОК 72 (maximumSize 72x72 у iOS);
      • фон круга white 20% — ОДИНАКОВЫЙ для on/off (у iOS нет разных
        заливок по состоянию тумблера);
      • иконка белая; у ВЫКЛЮЧЕННОГО тумблера — белая 70% (opacity на
        иконке, кнопка остаётся полностью активной);
      • отбой (danger) — красный круг ALGTheme.danger, иконка всегда
        непрозрачная;
      • имя кнопки — в title + aria-label (аналог accessibilityLabel iOS);
        видимого текста под кнопкой нет (Антон 05.10 08:51). */
function CallControlButton({
  testId,
  title,
  on,
  danger,
  offTone,
  disabled,
  onClick,
  children,
}: {
  testId: string;
  title: string;
  /** Включено ли управляемое состояние (микрофон/камера). Для «завершить»
      всегда true — у неё нет выключенного вида. */
  on: boolean;
  /** Красная кнопка разрушительного действия (отбой). */
  danger?: boolean;
  /**
   * КАК РИСОВАТЬ ВЫКЛЮЧЕННОЕ СОСТОЯНИЕ. Единственное значение — "red"
   * и единственный носитель — МИКРОФОН: выключенный человеком микрофон
   * красится красным (отдельная постановка Антона 12.09 10:29, НЕ
   * отменённая задачей 05.10 «1 в 1 к iPhone»: цена забытого микрофона —
   * говоришь в пустоту, поэтому его мьют подсвечен, как в Slack).
   * Все прочие тумблеры в выключенном виде — белая иконка 70% на белом
   * 20% круге, как в эталоне iOS ControlButton.
   */
  offTone?: "red";
  /** Временно недоступна (например, висит запрос доступа к камере).
      Защищает от второго getUserMedia и второго трека. */
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      aria-label={title}
      aria-pressed={danger ? undefined : on}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 56,
        height: 56,
        maxWidth: 72,
        maxHeight: 72,
        borderRadius: radius.circle,
        border: "none",
        /* ПРИГЛУШЁННОСТЬ ОЗНАЧАЕТ «НЕЛЬЗЯ НАЖАТЬ» И НИЧЕГО ДРУГОГО
           (правило Антона 12.09 10:24): единственный приглушённый вид —
           disabled. Выключенное состояние тумблера выражено ТОЛЬКО
           прозрачностью иконки (70%), сама кнопка живой яркости. */
        cursor: disabled ? "progress" : "pointer",
        opacity: disabled ? 0.45 : 1,
        display: "grid",
        placeItems: "center",
        padding: 0,
        /* Фон круга: white 20% как в iOS ControlButton (.white.opacity(0.2)).
           Отбой и выключенный микрофон — красный (color.danger темы). */
        background: danger || offTone === "red" ? color.danger : "rgba(255,255,255,0.20)",
        color: "#fff",
      }}
    >
      {/* Иконка: белая непрозрачная у активного состояния, у отбоя и у
          красного (выключенного) микрофона; белая 70% у остальных
          выключенных тумблеров (эталон iOS symbolOpacity = isOn ? 1 : 0.7). */}
      <span
        style={{
          display: "grid",
          placeItems: "center",
          lineHeight: 0,
          opacity: danger || offTone === "red" || on ? 1 : 0.7,
        }}
      >
        {children}
      </span>
    </button>
  );
}

/**
 * Меню выбора устройства у кнопки микрофона/камеры (задача 2026-09-13,
 * Антон 14:16: «нужен выбор устройств аудио и видео»).
 *
 * КОМПОНОВКА — как в Teams/Slack: маленькая стрелка ВПРАВО от основной
 * кнопки. Сама кнопка включает/выключает, стрелка открывает список.
 * Меню открывается ВВЕРХ (контролы звонка снизу экрана), поэтому
 * шеврон смотрит вверх (IconChevronUp).
 *
 * СОДЕРЖИМОЕ: одна или две группы устройств (у микрофона — «Микрофон» +
 * «Динамик», у камеры — только «Камера»). Первым пунктом каждой группы
 * идёт «По умолчанию» (deviceId "") — возврат к системному выбору.
 * Активное устройство отмечено галочкой слева; пустые label до выдачи
 * разрешения заменены на «Устройство N» ещё в rtc.ts (готча постановки).
 *
 * ЗАКРЫТИЕ: клик по пункту, клик вне меню (оверлей), Esc. Открытых меню
 * одновременно может быть два (микрофон и камера) — состояние локальное
 * у каждой стрелки, пересечений нет.
 */
function DeviceMenuButton({
  testId,
  title,
  groups,
  onSelect,
}: {
  testId: string;
  title: string;
  /** Группы устройств: заголовок + пункты + активный deviceId группы. */
  groups: {
    heading: string;
    kind: "mic" | "camera" | "speaker";
    items: { deviceId: string; label: string }[];
    activeId: string;
  }[];
  onSelect: (kind: "mic" | "camera" | "speaker", deviceId: string, label?: string) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Закрытие по Esc и по клику вне меню. Слушатели ставим только пока
  // меню открыто — закрытое меню не должно перехватывать ни клавиши,
  // ни клики (например, по соседней стрелке).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} style={{ position: "relative", display: "flex" }}>
      <button
        type="button"
        data-testid={testId}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 24,
          height: 48,
          borderRadius: radius.circle,
          border: "none",
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          // Тот же язык, что у основных контролов: полупрозрачная белая
          // подложка, при наведении чуть плотнее. Меньше по ширине — это
          // ВТОРИЧНАЯ кнопка, не соревнующая с основной.
          background: open ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.10)",
          color: "#fff",
        }}
      >
        <IconChevronUp size={14} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            bottom: 56,
            left: "50%",
            transform: "translateX(-50%)",
            minWidth: 240,
            maxWidth: 320,
            maxHeight: 320,
            overflowY: "auto",
            background: "rgba(28,28,30,0.97)",
            border: "1px solid rgba(255,255,255,0.16)",
            borderRadius: radius.md,
            padding: `${space.xs}px 0`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
            zIndex: 30,
          }}
        >
          {groups.map((group, gi) => (
            <div key={group.kind}>
              {gi > 0 && (
                <div
                  style={{
                    height: 1,
                    background: "rgba(255,255,255,0.12)",
                    margin: `${space.xs}px 0`,
                  }}
                />
              )}
              <div
                style={{
                  padding: `${space.xs}px ${space.lg}px`,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "rgba(255,255,255,0.55)",
                }}
              >
                {group.heading}
              </div>
              {/* «По умолчанию» — явный возврат к системному выбору
                  (deviceId ""). Без него выбравший гарнитуру человек не
                  смог бы вернуться на «как в системе», не очищая
                  localStorage руками. */}
              <DeviceMenuItem
                label={t("call.deviceDefault")}
                active={group.activeId === ""}
                onClick={() => {
                  onSelect(group.kind, "", "");
                  setOpen(false);
                }}
              />
              {group.items.map((d) => (
                <DeviceMenuItem
                  key={d.deviceId || `idx-${d.label}`}
                  label={d.label}
                  active={group.activeId !== "" && group.activeId === d.deviceId}
                  onClick={() => {
                    onSelect(group.kind, d.deviceId, d.label);
                    setOpen(false);
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Один пункт меню устройств: галочка активного + подпись. */
function DeviceMenuItem({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: space.sm,
        width: "100%",
        border: "none",
        background: active ? "rgba(255,255,255,0.12)" : "none",
        color: "#fff",
        fontSize: 13,
        textAlign: "left",
        padding: `${space.xs}px ${space.lg}px`,
        cursor: "pointer",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 16,
          flex: "0 0 auto",
          display: "inline-flex",
          justifyContent: "center",
          color: active ? color.accentBright : "transparent",
        }}
      >
        {/* Галочка активного устройства; у неактивных место зарезервировано,
            чтобы подписи стояли ровной колонкой. */}
        ✓
      </span>
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        {label}
      </span>
    </button>
  );
}

/**
 * Плашка звонка (MatrixRTC). Шаг 2 (постановка Момо 10.09 17:53): вход
 * со звуком по клику.
 *
 * Состояния показываем текстом, а не молчим:
 *  idle      — «В комнате идёт звонок · N» + кнопка «Присоединиться»
 *  joining   — «Подключаемся…», кнопки нет (двойной клик исключён)
 *  in-call   — «Вы в звонке · N участников» + кнопка «Выйти»
 *  error     — текст ошибки + кнопка «Попробовать снова»
 *
 * Микрофон запрашивается ТОЛЬКО из onJoin (клик человека) — см. rtc.ts.
 */
/**
 * Форма ключа «Вы в звонке · N участник(а/ов)» по числу. В русском три
 * формы, в остальных языках — две, поэтому выбор формы живёт в коде, а
 * слова — в словаре. Образец — `namesMoreKey` в `src/matrix/timeline.ts`.
 */
function inCallKey(
  n: number,
): "call.inCallOne" | "call.inCallFew" | "call.inCallMany" {
  if (getLangSnapshot() !== "ru") {
    return n === 1 ? "call.inCallOne" : "call.inCallMany";
  }
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "call.inCallMany";
  const mod10 = n % 10;
  if (mod10 === 1) return "call.inCallOne";
  if (mod10 >= 2 && mod10 <= 4) return "call.inCallFew";
  return "call.inCallMany";
}

function CallBanner({
  participants,
  state,
  error,
  onJoin,
  onLeave,
  onDecline,
  soloSecondsLeft,
  declinedBy,
}: {
  participants: number;
  state: "idle" | "joining" | "in-call" | "error";
  error: string | null;
  onJoin: () => void;
  onLeave: () => void;
  /**
   * Отказаться от чужого звонка прямо отсюда (Антон 16:07: «на плашке
   * внутри чата тоже должно быть две кнопки — принять и отказаться»).
   * Раньше отказ жил только во всплывающем баннере сверху, и человек,
   * промахнувшийся мимо него, был вынужден СНАЧАЛА войти в звонок и лишь
   * потом из него выйти. Необязателен: в комнатах без активного чужого
   * звонка отказываться не от чего.
   */
  onDecline?: () => void;
  /** Секунд до автовыхода, когда я позвонил и никто не ответил. */
  soloSecondsLeft?: number | null;
  /** Имя отклонившего мой звонок (MSC4310). */
  declinedBy?: string | null;
}) {
  const t = useT();
  const tfmt = useTF();
  const participantsWord =
    participants === 1 ? t("ui.uchastnik") : participants < 5 ? t("ui.uchastnika") : t("ui.uchastnikov");
  return (
    <div
      style={{
        margin: `${space.md}px ${space.lg}px 0`,
        padding: `10px ${space.lg}px`,
        borderRadius: radius.md,
        background: color.accentSoft,
        border: `1px solid ${state === "error" ? palette.danger : color.accent}`,
        display: "flex",
        alignItems: "center",
        gap: space.lg,
        fontSize: 13,
        color: color.text,
      }}
      data-testid="call-banner"
      data-call-state={state}
    >
      <span aria-hidden style={{ display: "inline-flex", color: color.accent }}>
        <IconPhone size={18} />
      </span>
      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
        {state === "idle" && (
          <>
            {tfmt("call.roomCallWithCount", {
              count: participants,
              word: participantsWord,
            })}
          </>
        )}
        {state === "joining" && t("ui.podklyuchaemsya_k_zvonku")}
        {/* Антон 19:00: «отмены нет». Плашка с ошибкой висела навсегда:
            единственной кнопкой было «Попробовать снова», то есть единственным
            выходом из ошибки была удачная попытка. Пока звонки чинятся,
            ошибка — частый исход, и человек обязан уметь её закрыть. */}
        {state === "in-call" && (
          <>
            {/* Один в звонке и никто не ответил — это «звоню и жду», а не
                разговор. Называть это «Вы в звонке» вводит в заблуждение. */}
            {declinedBy
              ? tfmt("call.declinedBy", { name: declinedBy })
              : soloSecondsLeft !== null && soloSecondsLeft !== undefined
                ? t("ui.id_t_zvonok_zhd_m_otveta")
                : tfmt(inCallKey(participants), { count: participants })}
            {soloSecondsLeft !== null && soloSecondsLeft !== undefined && (
              <span style={{ display: "block", fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                {tfmt("call.noAnswerCountdown", {
                  seconds: String(soloSecondsLeft).padStart(2, "0"),
                })}
              </span>
            )}
          </>
        )}
        {state === "error" && (
          <>{t("room.callConnectFailed")}<span style={{ display: "block", fontSize: 12, color: palette.danger, marginTop: 2 }}>
              {error}
            </span>
          </>
        )}
      </span>
      {/* ПРИНЯТЬ — ЗЕЛЁНАЯ ТРУБКА, БЕЗ НАДПИСИ (Антон 12.09 09:43: «принять
          везде и сделать зелёненькую, замени надписи на картинки...
          зелёненькая трубочка, красненькая трубочка во всех этих
          сообщениях»). Было: красная кнопка «Присоединиться» рядом с
          красной «Отказаться» — два одинаково красных прямоугольника,
          различимых только чтением. Антон: «когда сейчас две красных, это
          очень путает». */}
      {state === "idle" && (
        <button
          type="button"
          onClick={() => onJoin()}
          data-testid="call-join"
          title={t("room.headerCallJoin")}
          aria-label={t("room.headerCallJoin")}
          style={{
            flex: "0 0 auto",
            font: "inherit",
            width: 34,
            height: 34,
            borderRadius: radius.circle,
            border: "none",
            background: color.ok,
            color: color.textOnAccent,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconPhone size={17} />
        </button>
      )}
      {/* ОТКАЗАТЬСЯ — КРАСНАЯ ПЕРЕЧЁРКНУТАЯ ТРУБКА. Перечёркнутая, а не
          просто красная: цвет один не должен быть единственным отличием
          от «принять» (дальтонизм, чёрно-белая печать). */}
      {state === "idle" && onDecline && (
        <button
          type="button"
          onClick={() => onDecline()}
          data-testid="call-decline"
          title={t("ui.otkazatsya_ot_zvonka_zvonyaschiy_uznaet_ob")}
          aria-label={t("ui.otkazatsya_ot_zvonka")}
          style={{
            flex: "0 0 auto",
            font: "inherit",
            width: 34,
            height: 34,
            borderRadius: radius.circle,
            border: "none",
            // Мои кнопки отказа стояли на литерале #c0392b — второй
            // источник правды мимо палитры. Токен danger (этап 1).
            background: color.danger,
            color: color.textOnAccent,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconPhoneOff size={17} />
        </button>
      )}
      {state === "error" && (
        <button
          type="button"
          onClick={() => onJoin()}
          data-testid="call-join-retry"
          style={{
            flex: "0 0 auto",
            font: "inherit",
            fontSize: 13,
            padding: "6px 12px",
            borderRadius: radius.sm,
            border: "none",
            background: color.accent,
            color: color.textOnAccent,
            cursor: "pointer",
          }}
        >{t("common.tryAgain")}</button>
      )}
      {(state === "error" || state === "joining") && (
        <button
          type="button"
          onClick={onLeave}
          data-testid={state === "error" ? "call-dismiss" : "call-cancel"}
          title={state === "error" ? t("app.hideActionError") : t("ui.otmenit_podklyuchenie")}
          style={{
            flex: "0 0 auto",
            font: "inherit",
            fontSize: 13,
            padding: "6px 12px",
            borderRadius: radius.sm,
            border: `1px solid ${color.border}`,
            background: "transparent",
            color: color.textDim,
            cursor: "pointer",
          }}
        >
          {state === "error" ? "✕" : t("common.cancel")}
        </button>
      )}
      {state === "in-call" && (
        <button
          type="button"
          onClick={onLeave}
          data-testid="call-leave"
          /* ЭТАП 4: вид приведён к общему Teams-стилю — та же круглая
             красная кнопка с трубкой, что и «Завершить» на экране
             звонка. ЛОГИКА НЕ ТРОНУТА: режим «Отменить звонок» против
             «Выйти» по-прежнему выбирается soloSecondsLeft (Антон
             14:18), механика та же — leave(). Текст оставлен в title:
             круглая кнопка текста не несёт. */
          title={
            soloSecondsLeft !== null && soloSecondsLeft !== undefined
              ? t("ui.otmenit_zvonok")
              : t("room.headerCallLeave")
          }
          style={{
            flex: "0 0 auto",
            width: 40,
            height: 40,
            borderRadius: radius.circle,
            border: "none",
            background: color.danger,
            color: color.textOnAccent,
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
          }}
        >
          <IconPhoneOff size={18} />
        </button>
      )}
    </div>
  );
}

/**
 * Видеоэлемент одного тайла звонка (STEP6). Поток приходит из rtc.ts
 * (своя камера или чужая подписка). muted для своего превью — иначе эхо;
 * playsinline обязателен — без него Safari разворачивает видео на весь
 * экран и человек теряет интерфейс звонка.
 *
 * fit: "cover" для камеры (лицо заполняет плитку), "contain" для
 * демонстрации экрана (screen share 12:28, постановка дословно:
 * «object-fit: contain») — чужой рабочий стол нельзя обрезать:
 * обрезанная таблица/код не читаются, а пустые поля по бокам честны.
 */
function VideoElement({
  stream,
  muted,
  fit = "cover",
  zoomable = false,
  tileKey,
}: {
  stream: MediaStream;
  muted: boolean;
  /**
   * "tile16x9" — камера в плитке 16:9 (Антон 19.10 07:52: «boxes in group
   * call should be 16:9 not cropping the signal sent»). Решение по
   * ФАКТИЧЕСКИМ videoWidth/videoHeight (loadedmetadata + resize: телефон
   * поворачивается, трек меняет размер):
   *  - горизонтальный/квадратный кадр (16:9, 4:3, 1:1, ультраширокий) —
   *    целиком, object-fit: contain, остаток плитки — чёрные полосы;
   *  - вертикальный (высота > ширины) — вырезаем центральный квадрат
   *    (вся ширина кадра, середина по высоте) и ставим его по высоте
   *    плитки по центру, слева и справа чёрные полосы.
   */
  fit?: "cover" | "contain" | "tile16x9";
  /**
   * ВХОДЯЩАЯ демонстрация экрана (Антон 05.10 12:41/12:43: на чужом экране
   * мелкий текст не разобрать — картинку трансляции надо уметь УВЕЛИЧИВАТЬ).
   * Зум — общий Zoomable (src/ui/Zoomable.tsx), тот же механизм, что в
   * MediaViewer: щипок/колесо к точке, двойной тап-клик, протяжка в
   * увеличенном виде, пределы 1x…5x, края не уезжают. Своя исходящая
   * демонстрация и все камеры зума НЕ получают — флаг ставит CallTileView
   * только для kind==="screen" && !local.
   */
  zoomable?: boolean;
  /** Ключ плитки — смена трека размонтирует зум и сбрасывает масштаб. */
  tileKey?: string;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  // Форма кадра по фактическим размерам: null — размеры ещё неизвестны.
  const [portrait, setPortrait] = useState<boolean | null>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.srcObject = stream;
    }
    return () => {
      if (ref.current) ref.current.srcObject = null;
    };
  }, [stream]);
  useEffect(() => {
    if (fit !== "tile16x9") return;
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = el.videoWidth;
      const h = el.videoHeight;
      setPortrait(w > 0 && h > 0 ? h > w : null);
    };
    update();
    el.addEventListener("loadedmetadata", update);
    el.addEventListener("resize", update);
    return () => {
      el.removeEventListener("loadedmetadata", update);
      el.removeEventListener("resize", update);
    };
  }, [fit, stream]);
  if (fit === "tile16x9") {
    const shape = portrait === null ? "unknown" : portrait ? "portrait" : "landscape";
    const videoTag = (
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        data-video-shape={shape}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          // Вертикальный кадр внутри квадратной рамки: cover квадрата
          // = вся ширина кадра, середина по высоте. Остальное — contain.
          objectFit: portrait ? "cover" : "contain",
          objectPosition: "50% 50%",
        }}
      />
    );
    // Один и тот же <video> в обоих режимах (рамка — всегда div), чтобы
    // смена ориентации не пересоздавала элемент и не сбрасывала поток.
    return (
      <div
        data-testid="call-video-frame"
        style={portrait ? {
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          height: "100%",
          aspectRatio: "1 / 1",
          maxWidth: "100%",
          overflow: "hidden",
        } : {
          position: "absolute",
          inset: 0,
          overflow: "hidden",
        }}
      >
        {videoTag}
      </div>
    );
  }
  const video = (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      style={{
        // Zoomable центрирует содержимое гридом и сам задаёт transform;
        // width/height 100% внутри него превратили бы <video> в квадрат по
        // размеру обёртки и сломали замер natural-пропорций. Вписывание
        // делает max-ограничение + object-fit: contain.
        ...(zoomable
          ? { maxWidth: "100%", maxHeight: "100%" }
          : { width: "100%", height: "100%" }),
        objectFit: fit,
      }}
    />
  );
  if (!zoomable) return video;
  return (
    /* key=tileKey: сменился screen-трек (или демонстрация закончилась и
       плитка пересоздалась) — размонтируем обёртку целиком, масштаб
       сбрасывается в 1x. Когда демонстрация кончается совсем, плитка
       уходит из раскладки вместе с зумом. */
    <Zoomable key={tileKey}>
      {video}
    </Zoomable>
  );
}

/** Диалог подтверждения удаления сообщения. */
function DeleteMessageDialog({
  client,
  room,
  eventId,
  onClose,
}: {
  client: MatrixClient;
  room: Room;
  eventId: string;
  onClose: () => void;
}) {
  const t = useT();
  const { busy, error, run } = useAsyncAction(onClose);
  const item = buildTimeline(room, client.getUserId() ?? "").find((i) => i.eventId === eventId);
  const sourceEvent = room.getLiveTimeline().getEvents().find((ev) => ev.getId() === eventId);

  const isMine = item?.senderId === client.getUserId();
  const canDelete = sourceEvent ? canRedactEvent(client, room, sourceEvent) : false;

  if (!canDelete) {
    return (
      <Dialog
        title={t("ui.udalit_soobschenie")}
        onClose={onClose}
        error={t("ui.nedostatochno_prav_dlya_udaleniya_etogo_soo")}
        footer={
          <DialogButton onClick={onClose}>{t("common.close")}</DialogButton>
        }
      >
        <div style={{ fontSize: 13, color: color.text }}>
          {item ? item.body.slice(0, 200) : t("ui.soobschenie_ne_naydeno")}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={isMine ? t("ui.udalit_svo_soobschenie") : t("ui.udalit_soobschenie_moderatorom")}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="danger"
            disabled={busy}
            testId="delete-confirm"
            onClick={() =>
              run(async () => {
                try {
                  if (!sourceEvent) throw new Error(t("ui.soobschenie_ne_naydeno"));
                  await redactMessage(client, room, sourceEvent);
                } catch (e) {
                  throw new Error(humanError(e));
                }
              })
            }
          >{t("common.remove")}</DialogButton>
        </>
      }
    >
      <div style={{ fontSize: 13, color: color.text, lineHeight: 1.5 }}>
        {item ? (
          <>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{item.senderName}</div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 120,
                overflowY: "auto",
                padding: 8,
                background: color.sidebarBg,
                borderRadius: 6,
              }}
            >
              {item.body.slice(0, 500)}
            </div>
            {!isMine && (
              <div style={{ marginTop: 8, fontSize: 12, color: color.textDim }}>{t("room.moderatorDeleteNotice")}</div>
            )}
          </>
        ) : (
          t("ui.soobschenie_ne_naydeno_v_lente")
        )}
      </div>
    </Dialog>
  );
}
